/**
 * MindWorks ACL - Mail Inbox Review
 *
 * Current: v1.38 (2026-10-21)
 * Summary: Auto-file confirm says up to 50 when more rows are ready.
 * v1.37 (2026-10-13): esc delegates to Utils.escapeHtml.
 * v1.36 (2026-10-12): esc delegates to Utils.escapeHtml. Null and undefined stay empty.
 * v1.35 (2026-10-11): openQboLinkModal and openConfirmModal open through MWACL.Utils.mountModal.
 *          The three confirms opt out of drag and resize (400px, primary confirm). The QBO
 *          shell keeps the default. Footers use modalButton and wireModalAction. The
 *          confirm-button option and the QBO link button id are gone.
 * v1.34 (2026-09-21): Check chip reads "Check (QuickBooks)" when the number came from the
 *          QBO bill-payment lookup (checkNumberSource qbo_bill_payment); a stub with no
 *          number shows its remittance Ref chip; REF column falls back to checkReference;
 *          Details show Check # source + the lookup outcome (label from the API);
 *          new reason label check_stub_ready_to_file.
 * v1.33 (2026-09-21): Vendor-statement form searches QuickBooks by name (ap_search_qbo_vendors);
 *          Ids stay in state and are never rendered. Confident match auto-selects; a single
 *          like-match stays a radio the clerk clicks.
 * v1.32 (2026-09-21): missing_bill reason doors + credit-hold badge (slice 2b).
 * v1.31 (2026-09-21): Statements sub-filter (Unreviewed / Attention / Reviewed), Check now
 *          in-place coverage, findings panel, FINDING_LABELS / NOTE_LABELS.
 * v1.30 (2026-09-19): File-as vendor_statement / bank_statement, Statements tab (filed_unreviewed),
 *          Mark reviewed in-place, statement reason labels + line-item details.
 * v1.29 (2026-09-19): Queue Ref column shows the resolved Fishbowl PO (matchedPo — printed or via
 *          vendor order #) before check # / invoice #. Pairs with Admin API inboxReview
 *          at v2.22, where an 8-digit vendor order number in poReferences no longer counts as
 *          the printed PO (Hobby Lobby order 38376910 → PO 482820 via vendorSO), so
 *          File-as defaults to the PO folder and the PO suggester is seeded from vendorSO.
 * v1.28 (2026-09-19): Inspect — opens the scan in the site media viewer (MWACLMediaModal url mode:
 *          zoom/pan, Open in new tab → Drive). Pane zoom/Hand from v1.27 removed: the
 *          Drive /preview iframe is cross-origin and fits the page to natural size, so it
 *          can never be zoomed or panned by us. Preview region still builds once per
 *          document (iframe does not remount when the form below changes).
 */

(function() {
  'use strict';

  var MODULE_VERSION = 'v1.38';

  var AUTO_FILE_CAP = 50;

  var REASON_LABELS = {
    qbo_link_failed: 'Filed — QBO link failed (retry)',
    not_po_document: 'Not a PO invoice/receipt',
    not_po_invoice: 'Not a PO invoice/receipt', // legacy code (pre processDriveInbox v2.4) still stored on older docs
    no_po_reference: 'No PO number found',
    ready_to_file: 'Ready to file',
    multiple_po_references: 'Multiple POs on one file',
    low_confidence: 'Low confidence',
    potential_duplicate: 'Possible duplicate',
    po_not_in_fishbowl: 'PO not in Fishbowl',
    ambiguous_fishbowl_match: 'Ambiguous PO match',
    vendor_mismatch: 'Vendor does not match PO',
    invalid_po_number: 'Invalid PO number',
    no_check_number: 'No check number',
    check_stub_ready_to_file: 'Check # found — ready to file',
    deposit_must_be_single_check: 'Deposit must be one check',
    vendor_unresolved: 'Statement — vendor not yet known',
    no_statement_date: 'Statement — no close date',
    mixed_document: 'Mixed documents — split needed',
    bank_statement: 'Bank / card statement — choose account',
    statement_kind_unknown: 'Statement — vendor or bank?',
    vendor_folder_unresolved: 'Statement — confirm vendor folder',
    statement_shape_conflict: 'Looks like a statement — verify',
    statement_ready_to_file: 'Statement — vendor known, ready to file',
  };

  var FINDING_LABELS = {
    missing_bill: 'Not in QuickBooks',
    missing_po: 'No Fishbowl PO / receipt',
    amount_mismatch: 'Amount differs from QuickBooks',
    past_due_unpaid: 'Past due, unpaid',
    payment_not_credited: 'Paid, vendor has not credited it',
    credit_not_entered: 'Credit memo not entered',
    balance_mismatch: 'Statement balance ≠ QuickBooks',
  };

  var MISSING_BILL_REASON_LABELS = {
    po_partially_received: 'PO only partly received — cannot be billed yet',
    po_not_received: 'PO issued, nothing received yet',
    received_awaiting_reconcile: 'Received, not yet reconciled',
    reconciled_not_synced: 'Reconciled in Fishbowl, bill not posted to QuickBooks',
    invoice_in_review: 'We have the invoice — not yet billed',
    invoice_not_seen: 'Invoice never reached us',
  };

  var PO_STATUS_LABELS = {
    10: 'Bid Request',
    20: 'Issued',
    40: 'Partial',
    60: 'Fulfilled',
    70: 'Closed Short',
    80: 'Void',
  };

  var NOTE_LABELS = {
    received_not_reconciled: 'Received in Fishbowl, not yet reconciled',
    credit_unapplied: 'Credit in QuickBooks, not yet applied',
    payment_in_transit: 'Payment in transit',
    covered: 'In QuickBooks, agrees',
    covered_paid: 'Paid after the statement close',
  };

  var KIND_LABELS = { bill: 'Bill', check: 'Check', other: 'Other' };
  var KIND_BADGE = {
    bill: 'mw_acl-core-badge mw_acl-core-badge-info',
    check: 'mw_acl-core-badge mw_acl-core-badge-warning',
    other: 'mw_acl-core-badge mw_acl-core-badge-neutral',
  };

  var FILE_AS_LABELS = {
    po: 'PO invoice / receipt → PO folder',
    qbo_link: 'QBO bill (already entered) → Vendors folder',
    check_stubs: 'Check stub (we paid a vendor) → Check Stubs',
    deposits: 'Customer payment (they paid us) → Deposits',
    vendor_statement: 'Vendor statement → Vendors folder (not a bill)',
    bank_statement: 'Bank / card statement → Banking/{Account}',
  };
  var FILE_AS_ORDER = ['po', 'qbo_link', 'check_stubs', 'deposits', 'vendor_statement', 'bank_statement'];
  var STATEMENT_KIND_LABELS = { vendor: 'Vendor', bank: 'Bank', card: 'Card', loc: 'Line of credit' };

  // Reasons that mean "this bill has no Fishbowl PO" — the PO default must not fire
  var NO_PO_REASONS = { no_po_reference: true, not_po_document: true, not_po_invoice: true };
  var QBO_MODAL_ID = 'ap-inbox-qbo-link-modal';
  var HALF_STATE_REASON = 'qbo_link_failed';
  var FILING_STATUS_NOTE = {
    mapped: '(mapped)',
    adopt: '(adopts existing folder)',
    create: '(creates folder)',
    filed: '(already filed)',
  };

  // Why a candidate matched — spec §7: explain, don't just badge
  var SIGNAL_LABELS = {
    items: 'items \u2713',
    qty: 'qty \u2713',
    price: 'price \u2713',
    subtotal: 'subtotal \u2713',
    total: 'total \u2713',
    card_charge: 'card charge \u2713',
    amount: 'amount \u2713',
    gl_note: 'GL note \u2713',
    po_on_document: 'PO printed on document \u2713',
    handwritten_po: 'handwritten PO',
    handwritten_po_unclear: 'handwritten PO (unclear)',
    handwritten_resolved: 'handwritten PO \u2713 (struck digit resolved)',
    shipment_amount: 'shipment amount \u2713',
    shipment_qty: 'shipment qty \u2713',
    card_ref: 'card marker \u2713',
    tracking: 'tracking \u2713',
    already_reconciled: 'shipment already reconciled',
    price_variance_at_reconciliation: 'billed \u2260 ordered (variance)',
    ai_corroborated: 'AI same purchase',
    vendor_via_remit: 'vendor matched via remit-to',
    marketplace_platform: 'marketplace platform',
  };

  var BASIS_LABELS = {
    billed: 'billed cost',
    landed: 'landed cost',
    ordered: 'ordered cost',
  };

  var TAX_NOTE_LABELS = {
    tax_not_on_po: 'tax not on PO \u2014 expected at reconciliation',
    tax_line_on_po: 'tax entered as a PO line',
    tax_in_unit_cost: 'tax appears baked into unit cost \u2014 verify',
    tip_unaccounted: 'tip may be unaccounted \u2014 verify',
  };

  var initialized = false;
  var searchTimeout = null;
  var state = {
    items: [],
    stats: { needsReview: 0, filedStatements: 0, needsAttention: 0 },
    view: 'needs_review',
    stmtStatus: 'filed_unreviewed',
    checkingCoverage: null,
    pagination: { hasMore: false, nextCursor: null },
    loading: true,
    searchTerm: '',
    selectedId: null,
    selectedIds: new Set(),
    inspect: null,
    candidates: [],
    noMatch: null,
    candidatesLoading: false,
    candidatesForId: null,
    filing: false,
    removing: false,
    reclassifying: false,
    autofiling: false,
    pulling: false,
    chosenPo: '',
    kindFilter: 'all',
    fileAs: '',
    showDetails: false,
    previewExpanded: false,
    checkNumber: '',
    payee: '',
    reference: '',
    checkDate: '',
    isBatch: false,
    // Q2 qbo_link form + modal
    qbo: { vendorName: '', vendorId: '', docNumber: '', txnDate: '', total: '' },
    qboFinding: false,
    qboPreview: null,        // last ap_link_qbo_entry preview payload (modal source)
    qboSelectedTxnId: '',
    qboFolderId: '',
    qboFileTo: '',           // 'po' | 'vendor' — set per chosen candidate (PO default when its memo PO verified)
    stmt: { vendorId: '', vendorDisplayName: '', closeDate: '', accountFolderId: '', accountName: '', kind: 'bank', folderId: '', forceCreate: false, filing: null, vendorSearch: { query: '', searchedFor: '', loading: false, candidates: null, confident: null, error: null } },
    bankingAccounts: null,
    bankingAccountsLoading: false,
    markingReviewed: null,
  };

  function emptyStmt(item) {
    var st = (item && item.statement) || {};
    var sk = item && (item.statementKind || st.kind);
    return {
      vendorId: '',
      vendorDisplayName: (item && item.vendor) || '',
      closeDate: st.closeDate || st.periodEnd || '',
      accountFolderId: '',
      accountName: '',
      kind: (sk === 'card' || sk === 'loc' || sk === 'bank') ? sk : 'bank',
      folderId: '',
      forceCreate: false,
      filing: null,
      vendorSearch: {
        query: (item && item.vendor) || '',
        searchedFor: '',
        loading: false,
        candidates: null,
        confident: null,
        error: null,
      },
    };
  }

  function toast(msg, type) {
    if (window.MWACL && window.MWACL.Utils && window.MWACL.Utils.showToast) {
      window.MWACL.Utils.showToast(msg, type || 'info');
    }
  }

  const esc = window.MWACL.Utils.escapeHtml;

  function reasonLabel(reason) {
    if (!reason) return 'Needs review';
    if (REASON_LABELS[reason]) return REASON_LABELS[reason];
    // Unknown/legacy code: humanize "some_old_code" → "Some old code"
    var words = String(reason).replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  function itemKind(item) {
    return item && item.queueKind ? item.queueKind : 'other';
  }

  function kindLabel(kind) {
    return KIND_LABELS[kind] || 'Other';
  }

  // Ref column: the resolved FB PO first (printed OR via vendor order — Admin API
  // matchedPo), then check # / invoice #. A vendor's own 8-digit order number is
  // never a PO, so it no longer appears here as one (Hobby Lobby 38376910 → 482820).
  // v1.34: a check stub with the number cropped still carries its remittance
  // reference (the vendor invoice #) — show that rather than a blank REF.
  function itemRef(item) {
    return item.matchedPo || item.extractedPo || item.checkNumber || item.invoiceNumber || item.checkReference || '';
  }

  var CHECK_SOURCE_LABELS = {
    classifier: 'read from the stub',
    qbo_bill_payment: 'found in QuickBooks',
    human: 'entered by reviewer',
  };

  // True when the extracted PO came from handwriting the classifier marked
  // legible: false (strike-through / overwrite / uncertain digits)
  function isUnclearRef(item, core) {
    var refs = (item && item.poReferences) || [];
    return refs.some(function(r) {
      return r && r.coreNumber === core && r.source === 'handwritten' && r.legible === false;
    });
  }

  function money(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return null;
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // The FB core the row resolved to: matchedPo (printed_po OR vendor_order — the
  // Admin API's chosen core) with extractedPo as the pre-v2.19 fallback.
  function rowMatchedPo(item) {
    var po = item && (item.matchedPo || item.extractedPo);
    return /^\d{6}$/.test(String(po || '')) ? String(po) : '';
  }

  // Q2 default table: PO when the row resolved to a 6-digit FB core (printed on
  // the page OR matched via the vendor's order # ↔ Fishbowl vendorSO) AND the
  // classifier did not say "no PO"; otherwise the bill is already in QBO by hand.
  // The PO is the originating record — a bill that traces to one never defaults
  // to the QBO-bill door.
  function defaultFileAs(item) {
    if (!item) return '';
    if (item.documentType === 'STATEMENT') {
      var sk = item.statementKind || (item.statement && item.statement.kind);
      return (sk === 'bank' || sk === 'card' || sk === 'loc') ? 'bank_statement' : 'vendor_statement';
    }
    var kind = itemKind(item);
    if (kind === 'bill') {
      if (item.reviewReason === HALF_STATE_REASON) return 'qbo_link';
      if (rowMatchedPo(item) && !NO_PO_REASONS[item.reviewReason]) return 'po';
      return 'qbo_link';
    }
    if (kind === 'check') {
      return item.documentType === 'CUSTOMER_PAYMENT' ? 'deposits' : 'check_stubs';
    }
    return ''; // other/unknown — human chooses
  }

  function init() {
    if (initialized) return;
    if (window.MWACLPageAccessDenied) return;

    var container = document.getElementById('mw-ap-inbox-review-app');
    if (container) container.style.display = 'block';

    bindEvents();
    loadData();
    initialized = true;
  }

  function bindEvents() {
    var searchInput = document.getElementById('ap-inbox-search');
    if (searchInput) {
      searchInput.addEventListener('input', function(e) {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(function() {
          state.searchTerm = e.target.value.trim().toLowerCase();
          renderTable();
        }, 300);
      });
    }

    document.querySelectorAll('#ap-inbox-kind-tabs .mw_acl-core-tab').forEach(function(tab) {
      tab.addEventListener('click', function() {
        var kind = tab.getAttribute('data-kind') || 'all';
        document.querySelectorAll('#ap-inbox-kind-tabs .mw_acl-core-tab').forEach(function(t) {
          t.classList.toggle('mw_acl-core-tab-active', t === tab);
        });
        if (kind === 'statements') {
          if (state.view !== 'filed_statements') {
            state.view = 'filed_statements';
            state.kindFilter = 'all';
            state.selectedId = null;
            loadData();
          }
          return;
        }
        state.kindFilter = kind;
        if (state.view === 'filed_statements') {
          state.view = 'needs_review';
          state.selectedId = null;
          loadData();
          return;
        }
        renderTable();
      });
    });

    var pullBtn = document.getElementById('ap-inbox-pull');
    if (pullBtn) pullBtn.addEventListener('click', confirmPullInbox);

    var reclassifyBtn = document.getElementById('ap-inbox-reclassify');
    if (reclassifyBtn) reclassifyBtn.addEventListener('click', handleReclassify);

    var autofileBtn = document.getElementById('ap-inbox-autofile');
    if (autofileBtn) autofileBtn.addEventListener('click', handleAutoFileReady);

    document.addEventListener('keydown', handleKeydown);
  }

  // === Keyboard: Up/Down navigate (J/K aliases), Enter files ===
  function handleKeydown(e) {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    var tag = (e.target && e.target.tagName || '').toLowerCase();
    // Queue checkboxes keep focus after a click — let arrows/J/K still walk the queue,
    // but never let Enter on a checkbox file the previewed document.
    var isCheckbox = tag === 'input' && e.target.type === 'checkbox';
    if ((tag === 'input' && !isCheckbox) || tag === 'textarea' || tag === 'select' || (e.target && e.target.isContentEditable)) return;

    // Confirm modal open: queue keys must not move/file behind it (Escape/click handled by MWACL.Modal)
    if (document.getElementById('ap-inbox-confirm-not-ours') ||
        document.getElementById('ap-inbox-confirm-pull') ||
        document.getElementById('ap-inbox-confirm-autofile') ||
        document.getElementById(QBO_MODAL_ID)) return;

    var key = e.key;
    var lower = key.toLowerCase();
    if (key === 'ArrowDown' || lower === 'j') {
      e.preventDefault();
      moveSelection(1);
    } else if (key === 'ArrowUp' || lower === 'k') {
      e.preventDefault();
      moveSelection(-1);
    } else if (lower === 'enter' && !isCheckbox && state.selectedId && !isBusy()) {
      e.preventDefault();
      if (state.fileAs === 'po') handleFile();
      else if (state.fileAs === 'qbo_link') handleQboLinkPreview();
      else if (state.fileAs === 'check_stubs' || state.fileAs === 'deposits') handleFileBanking(state.fileAs);
      else if (state.fileAs === 'vendor_statement' || state.fileAs === 'bank_statement') handleFileStatement(state.fileAs);
      else toast('Choose a destination in "File as" first', 'info');
    }
  }

  function moveSelection(delta) {
    var items = filteredItems();
    if (!items.length) return;
    var idx = items.findIndex(function(i) { return i.id === state.selectedId; });
    var next;
    if (idx === -1) {
      next = items[0];
    } else {
      next = items[Math.min(items.length - 1, Math.max(0, idx + delta))];
    }
    if (next && next.id !== state.selectedId) selectDoc(next.id);
  }

  function loadData(cursor) {
    state.loading = true;
    if (!cursor) {
      state.items = [];
      renderLoading();
    }

    var params = { limit: 25 };
    if (cursor) params.cursor = cursor;
    if (state.view === 'filed_statements') {
      params.view = 'filed_statements';
      params.statementStatus = state.stmtStatus || 'filed_unreviewed';
    }

    window.MWACL.API.call('ap_list_inbox_review', params)
      .then(function(response) {
        var data = response.data || response;
        state.stats = data.stats || state.stats;
        if (cursor) {
          state.items = state.items.concat(data.items || []);
        } else {
          state.items = data.items || [];
        }
        var keep = new Set();
        state.items.forEach(function(i) {
          if (state.selectedIds.has(i.id)) keep.add(i.id);
        });
        state.selectedIds = keep;
        state.pagination = data.pagination || { hasMore: false, nextCursor: null };
        state.loading = false;
        renderStats();
        renderTable();
        renderPagination();
        if (state.selectedId && !state.items.some(function(i) { return i.id === state.selectedId; })) {
          state.selectedId = null;
          renderPane();
        }
      })
      .catch(function(err) {
        state.loading = false;
        console.error('[ap-inbox-review] Load error:', err);
        renderError(err.message || 'Failed to load review queue');
      });
  }

  function renderStats() {
    var badge = document.getElementById('ap-inbox-count');
    if (badge) {
      var n = state.stats.needsReview || 0;
      badge.textContent = n + ' to review';
      badge.classList.toggle('ap-inbox-count-zero', n === 0);
    }
    var stmtBadge = document.getElementById('ap-inbox-statements-count');
    if (stmtBadge) {
      var total = (state.stats.filedStatements || 0) + (state.stats.needsAttention || 0);
      stmtBadge.textContent = total ? String(total) : '';
    }
    var attBadge = document.getElementById('ap-inbox-attention-count');
    if (attBadge) {
      var att = state.stats.needsAttention || 0;
      attBadge.textContent = att ? String(att) : '';
    }
  }

  function renderLoading() {
    var container = document.getElementById('ap-inbox-table');
    if (!container) return;
    container.innerHTML = '<div class="mw_acl-core-loading"><div class="mw_acl-core-spinner"></div><p>Loading...</p></div>';
  }

  function renderError(msg) {
    var container = document.getElementById('ap-inbox-table');
    if (!container) return;
    container.innerHTML = '<div class="mw_acl-core-empty-state"><p>' + esc(msg) + '</p>' +
      '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-secondary" id="ap-inbox-retry">Retry</button></div>';
    var retry = document.getElementById('ap-inbox-retry');
    if (retry) retry.addEventListener('click', function() { loadData(); });
  }

  function filteredItems() {
    return state.items.filter(function(item) {
      if (state.kindFilter !== 'all' && itemKind(item) !== state.kindFilter) return false;
      if (!state.searchTerm) return true;
      var q = state.searchTerm;
      return [item.fileName, item.vendor, item.extractedPo, item.matchedPo, item.reviewReason, item.invoiceNumber, item.checkNumber, item.bankAccountName, item.qboVendorDisplayName, item.closeDate, item.statementKind]
        .join(' ')
        .toLowerCase()
        .indexOf(q) !== -1;
    });
  }

  function renderTable() {
    var container = document.getElementById('ap-inbox-table');
    if (!container) return;

    var items = filteredItems();
    if (items.length === 0 && !state.loading) {
      if (state.view === 'filed_statements') {
        container.innerHTML = renderStmtStatusTabs() +
          '<div class="mw_acl-core-empty-state"><p>No statements in this view.</p></div>';
        container.querySelectorAll('#ap-inbox-stmt-status-tabs .mw_acl-core-tab').forEach(function(tab) {
          tab.addEventListener('click', function() {
            var next = tab.getAttribute('data-stmt-status') || 'filed_unreviewed';
            if (state.stmtStatus === next) return;
            state.stmtStatus = next;
            state.selectedId = null;
            loadData();
          });
        });
      } else {
        container.innerHTML = '<div class="mw_acl-core-empty-state"><p>No documents in the filing review queue.</p></div>';
      }
      syncSelectAll();
      renderReclassifyLabel();
      return;
    }

    if (state.view === 'filed_statements') {
      renderFiledStatementsTable(container, items);
      return;
    }

    var html = '<div class="mw_acl-core-table-container"><table class="mw_acl-core-table ap-inbox-table">';
    html += '<thead><tr>';
    html += '<th class="mw_acl-core-table-cb"><label class="mw_acl-core-table-cb-label"><input type="checkbox" id="ap-inbox-select-all" aria-label="Select all visible documents"></label></th>';
    html += '<th>Type</th><th>Name</th><th>Ref</th><th>Reason</th></tr></thead><tbody>';
    items.forEach(function(item) {
      var kind = itemKind(item);
      var active = item.id === state.selectedId ? ' ap-inbox-row-active' : '';
      html += '<tr class="ap-inbox-row' + active + '" data-doc-id="' + esc(item.id) + '">';
      html += '<td class="mw_acl-core-table-cb"><label class="mw_acl-core-table-cb-label"><input type="checkbox" class="ap-inbox-row-check" data-doc-id="' + esc(item.id) + '" aria-label="Select for reclassify"' + (state.selectedIds.has(item.id) ? ' checked' : '') + '></label></td>';
      html += '<td data-label="Type"><span class="' + KIND_BADGE[kind] + '">' + esc(kindLabel(kind)) + '</span></td>';
      html += '<td data-label="Name" class="ap-inbox-cell-clip" title="' + esc(item.vendor || item.fileName || '') + '">' + esc(item.vendor || '—') + '</td>';
      html += '<td data-label="Ref">' + esc(itemRef(item) || '—') + '</td>';
      html += '<td data-label="Reason" class="ap-inbox-cell-clip" title="' + esc(reasonLabel(item.reviewReason)) + '">' + esc(reasonLabel(item.reviewReason)) + '</td>';
      html += '</tr>';
    });
    html += '</tbody></table></div>';
    container.innerHTML = html;

    container.querySelectorAll('.ap-inbox-row').forEach(function(row) {
      row.addEventListener('click', function(e) {
        if (e.target.closest('.mw_acl-core-table-cb')) return;
        selectDoc(row.getAttribute('data-doc-id'));
      });
    });

    container.querySelectorAll('.ap-inbox-row-check').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var id = cb.getAttribute('data-doc-id');
        if (cb.checked) state.selectedIds.add(id);
        else state.selectedIds.delete(id);
        syncSelectAll();
        renderReclassifyLabel();
      });
    });

    var selectAll = document.getElementById('ap-inbox-select-all');
    if (selectAll) {
      selectAll.addEventListener('change', function() {
        var checked = selectAll.checked;
        filteredItems().forEach(function(item) {
          if (checked) state.selectedIds.add(item.id);
          else state.selectedIds.delete(item.id);
        });
        container.querySelectorAll('.ap-inbox-row-check').forEach(function(cb) {
          cb.checked = checked;
        });
        syncSelectAll();
        renderReclassifyLabel();
      });
    }

    syncSelectAll();
    renderReclassifyLabel();
  }

  function statementStatusBadgeHtml(item) {
    var cov = item.statementCoverage || {};
    var n = Array.isArray(cov.findings) ? cov.findings.length : 0;
    if (item.statementStatus === 'needs_attention') {
      var html = '';
      if (item.statementCoverage && item.statementCoverage.risk === 'credit_hold') {
        html += '<span class="mw_acl-core-badge mw_acl-core-badge-failed">Credit-hold risk</span> ';
      }
      html += '<span class="mw_acl-core-badge mw_acl-core-badge-warning">Attention · ' + n + ' finding' + (n === 1 ? '' : 's') + '</span>';
      return html;
    }
    if (item.statementStatus === 'reviewed') {
      return '<span class="mw_acl-core-badge mw_acl-core-badge-active">Reviewed</span>';
    }
    return '<span class="mw_acl-core-badge mw_acl-core-badge-neutral">Unchecked</span>';
  }

  function filedStatementActionsHtml(item) {
    var checking = state.checkingCoverage === item.id;
    var pending = state.markingReviewed === item.id;
    var busy = isBusy();
    var html = '';
    html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-sm mw_acl-core-btn-secondary ap-inbox-check-now" data-id="' + esc(item.id) + '"' +
      (checking || busy ? ' disabled' : '') + '>' + (checking ? 'Checking…' : 'Check now') + '</button>';
    // Mark reviewed is the human override — already-reviewed rows have nothing to override
    if (item.statementStatus !== 'reviewed') {
      html += ' <button type="button" class="mw_acl-core-btn mw_acl-core-btn-sm mw_acl-core-btn-secondary ap-inbox-mark-reviewed" data-doc-id="' + esc(item.id) + '"' +
        (pending || busy ? ' disabled' : '') + '>' + (pending ? '…' : 'Mark reviewed') + '</button>';
    }
    return html;
  }

  function renderStmtStatusTabs() {
    var att = state.stats.needsAttention || 0;
    var tabs = [
      { id: 'filed_unreviewed', label: 'Unreviewed' },
      { id: 'needs_attention', label: 'Attention' },
      { id: 'reviewed', label: 'Reviewed' },
    ];
    var html = '<div class="mw_acl-core-tabs ap-inbox-stmt-status-tabs" id="ap-inbox-stmt-status-tabs" role="tablist" aria-label="Statement status">';
    tabs.forEach(function(tab) {
      var active = (state.stmtStatus || 'filed_unreviewed') === tab.id;
      html += '<button type="button" class="mw_acl-core-tab' + (active ? ' mw_acl-core-tab-active' : '') + '" data-stmt-status="' + tab.id + '">' +
        esc(tab.label);
      if (tab.id === 'needs_attention') {
        html += ' <span class="mw_acl-core-tab-badge" id="ap-inbox-attention-count">' + (att ? String(att) : '') + '</span>';
      }
      html += '</button>';
    });
    html += '</div>';
    return html;
  }

  function bindFiledStatementRowEvents(root) {
    root.querySelectorAll('.ap-inbox-row').forEach(function(row) {
      row.addEventListener('click', function(e) {
        if (e.target.closest('.mw_acl-core-table-actions')) return;
        selectDoc(row.getAttribute('data-id') || row.getAttribute('data-doc-id'));
      });
    });
    bindFiledStatementActions(root);
  }

  function bindFiledStatementActions(root) {
    root.querySelectorAll('.ap-inbox-mark-reviewed').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        handleMarkReviewed(btn.getAttribute('data-doc-id'), btn);
      });
    });
    root.querySelectorAll('.ap-inbox-check-now').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        handleCheckNow(btn.getAttribute('data-id'), btn);
      });
    });
  }

  function renderFiledStatementsTable(container, items) {
    var html = renderStmtStatusTabs();
    html += '<div class="mw_acl-core-table-container"><table class="mw_acl-core-table ap-inbox-table ap-inbox-stmt-table">';
    html += '<thead><tr><th>Vendor / Account</th><th>Kind</th><th>Close date</th><th>Amount due</th><th>Status</th><th>Filed to</th><th class="mw_acl-core-table-actions">Actions</th></tr></thead><tbody>';
    items.forEach(function(item) {
      var who = item.qboVendorDisplayName || item.bankAccountName || item.vendor || '—';
      var active = item.id === state.selectedId ? ' ap-inbox-row-active' : '';
      html += '<tr class="ap-inbox-row' + active + '" data-id="' + esc(item.id) + '" data-doc-id="' + esc(item.id) + '">';
      html += '<td data-label="Vendor / Account" class="ap-inbox-cell-clip" title="' + esc(who) + '">' + esc(who) + '</td>';
      html += '<td data-label="Kind">' + esc(STATEMENT_KIND_LABELS[item.statementKind] || item.statementKind || '—') + '</td>';
      html += '<td data-label="Close date">' + esc(item.closeDate || '—') + '</td>';
      html += '<td data-label="Amount due">' + esc(money(item.amountDue) || '—') + '</td>';
      html += '<td data-label="Status">' + statementStatusBadgeHtml(item) + '</td>';
      html += '<td data-label="Filed to" class="ap-inbox-cell-clip" title="' + esc(item.destinationPath || '') + '">' + esc(item.destinationPath || '—') + '</td>';
      html += '<td class="mw_acl-core-table-actions">' + filedStatementActionsHtml(item) + '</td>';
      html += '</tr>';
    });
    html += '</tbody></table></div>';
    container.innerHTML = html;

    container.querySelectorAll('#ap-inbox-stmt-status-tabs .mw_acl-core-tab').forEach(function(tab) {
      tab.addEventListener('click', function() {
        var next = tab.getAttribute('data-stmt-status') || 'filed_unreviewed';
        if (state.stmtStatus === next) return;
        state.stmtStatus = next;
        state.selectedId = null;
        loadData();
      });
    });
    bindFiledStatementRowEvents(container);
  }

  function updateFiledStatementRow(item) {
    var row = document.querySelector('.ap-inbox-row[data-id="' + item.id + '"]');
    if (!row) return;
    var statusCell = row.querySelector('[data-label="Status"]');
    if (statusCell) statusCell.innerHTML = statementStatusBadgeHtml(item);
    var actions = row.querySelector('.mw_acl-core-table-actions');
    if (actions) {
      actions.innerHTML = filedStatementActionsHtml(item);
      bindFiledStatementActions(actions);
    }
  }

  function applyCoverageStatusDelta(prevStatus, nextStatus) {
    if (prevStatus === nextStatus) return;
    if (prevStatus === 'filed_unreviewed') state.stats.filedStatements = Math.max(0, (state.stats.filedStatements || 1) - 1);
    if (prevStatus === 'needs_attention') state.stats.needsAttention = Math.max(0, (state.stats.needsAttention || 1) - 1);
    if (nextStatus === 'filed_unreviewed') state.stats.filedStatements = (state.stats.filedStatements || 0) + 1;
    if (nextStatus === 'needs_attention') state.stats.needsAttention = (state.stats.needsAttention || 0) + 1;
    renderStats();
  }

  function syncSelectAll() {
    var selectAll = document.getElementById('ap-inbox-select-all');
    if (!selectAll) return;
    var visible = filteredItems();
    var n = visible.length;
    var selected = 0;
    visible.forEach(function(item) {
      if (state.selectedIds.has(item.id)) selected += 1;
    });
    selectAll.checked = n > 0 && selected === n;
    selectAll.indeterminate = selected > 0 && selected < n;
  }

  function renderReclassifyLabel() {
    if (state.reclassifying) return;
    var btn = document.getElementById('ap-inbox-reclassify');
    if (!btn) return;
    var n = state.selectedIds.size;
    btn.textContent = n ? ('Reclassify (' + n + ')') : 'Reclassify';
  }

  function renderPagination() {
    var container = document.getElementById('ap-inbox-pagination');
    if (!container) return;
    if (!state.pagination.hasMore) {
      container.innerHTML = '';
      return;
    }
    container.innerHTML = '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-secondary" id="ap-inbox-more">Load more</button>';
    document.getElementById('ap-inbox-more').addEventListener('click', function() {
      loadData(state.pagination.nextCursor);
    });
  }

  function selectDoc(docId) {
    state.selectedId = docId;
    state.chosenPo = '';
    state.showDetails = false;
    if (state.candidatesForId !== docId) {
      state.candidates = [];
      state.noMatch = null;
      state.candidatesForId = null;
    }
    var item = state.items.find(function(i) { return i.id === docId; });
    // Pre-fill the PO input from the AI's read — but NOT from unclear handwriting
    // (legible: false). An uncertain scrawl must never sit one Enter away from
    // filing; the candidate list will still surface it with its warning signals.
    // A vendor_order match (matchedPo with no extractedPo) prefills too — it
    // came from Fishbowl vendorSO, not from handwriting.
    var rowPo = rowMatchedPo(item);
    if (rowPo && !isUnclearRef(item, rowPo)) {
      state.chosenPo = rowPo;
    }
    state.checkNumber = (item && item.checkNumber) || '';
    state.payee = (item && item.vendor) || '';
    state.reference = (item && (item.checkReference || item.invoiceNumber)) || '';
    state.checkDate = (item && (item.checkDate || item.invoiceDate)) || '';
    state.isBatch = !!(item && item.isBatch);
    // qbo_link form prefill from the classifier (vendor Id stays empty — human fix-up field)
    state.qbo = {
      vendorName: (item && item.vendor) || '',
      vendorId: '',
      docNumber: (item && item.invoiceNumber) || '',
      txnDate: (item && item.invoiceDate) || '',
      total: item && item.total != null ? String(item.total) : '',
    };
    state.qboPreview = null;
    state.qboSelectedTxnId = '';
    state.qboFolderId = '';
    state.qboFileTo = '';
    state.stmt = emptyStmt(item);
    state.fileAs = state.view === 'filed_statements' ? '' : defaultFileAs(item);
    renderTable();
    renderPane();
    if (state.fileAs === 'po') {
      loadCandidates(docId);
    }
  }

  function loadCandidates(docId) {
    if (state.candidatesForId === docId) return; // already loaded for this doc
    state.candidates = [];
    state.noMatch = null;
    state.candidatesLoading = true;
    state.candidatesForId = docId;
    renderPane();
    window.MWACL.API.call('ap_suggest_nearby_pos', { docId: docId })
      .then(function(response) {
        if (state.selectedId !== docId) return; // user moved on
        var data = response.data || response;
        state.candidates = data.candidates || [];
        state.noMatch = data.noMatch || null;
        state.candidatesLoading = false;
        // Auto-select only a HIGH-tier candidate — a medium "possible match"
        // must not be one Enter away from filing (spec §7)
        if (!state.chosenPo && state.candidates.length && state.candidates[0].matchTier === 'high' &&
            !state.candidates[0].annotationConflict) {
          state.chosenPo = state.candidates[0].num;
        }
        renderPane();
      })
      .catch(function(err) {
        if (state.selectedId !== docId) return;
        state.candidatesLoading = false;
        console.error('[ap-inbox-review] Suggest error:', err);
        renderPane();
        toast(err.message || 'Could not load PO suggestions', 'warning');
      });
  }

  function selectedItem() {
    return state.items.find(function(i) { return i.id === state.selectedId; }) || null;
  }

  // === Pane: hero preview (stable per doc) + chips + file-as selector + one form ===
  function renderPane() {
    var pane = document.getElementById('ap-inbox-pane');
    if (!pane) return;
    var item = selectedItem();
    if (!item) {
      pane.innerHTML = '<div class="mw_acl-core-empty-state"><p>Select a document to preview and file it to a PO, Check Stubs, or Deposits — or mark Not ours.</p></div>';
      pane.removeAttribute('data-preview-doc');
      return;
    }

    var previewRegion = document.getElementById('ap-inbox-preview-region');
    var paneBody = document.getElementById('ap-inbox-pane-body');
    if (!previewRegion || !paneBody) {
      pane.innerHTML = '<div id="ap-inbox-preview-region"></div><div id="ap-inbox-pane-body"></div>';
      pane.removeAttribute('data-preview-doc'); // fresh shell → always rebuild the preview
      previewRegion = document.getElementById('ap-inbox-preview-region');
      paneBody = document.getElementById('ap-inbox-pane-body');
    }

    if (pane.getAttribute('data-preview-doc') !== item.id) {
      renderPreviewRegion(item);
      pane.setAttribute('data-preview-doc', item.id);
      bindPreviewEvents();
    }

    var html = '';

    // Chips: what the AI read, one line
    var kind = itemKind(item);
    html += '<div class="ap-inbox-chips">';
    html += '<span class="' + KIND_BADGE[kind] + '">' + esc(kindLabel(kind)) + '</span>';
    if (item.vendor) {
      html += '<span class="ap-inbox-chip">' + esc(item.vendor) +
        (item.marketplaceSeller ? ' (seller: ' + esc(item.marketplaceSeller) + ')' : '') +
        '</span>';
    }
    if (item.checkNumber) {
      // v1.34: provenance — a number QuickBooks supplied (cropped stub) reads differently
      // from one the classifier read off the page.
      var fromQbo = item.checkNumberSource === 'qbo_bill_payment';
      html += '<span class="ap-inbox-chip' + (fromQbo ? ' ap-inbox-chip-qbo' : '') + '"' +
        (fromQbo ? ' title="Check number found in QuickBooks from the remittance reference"' : '') + '>' +
        '<span class="ap-inbox-chip-label">' + (fromQbo ? 'Check (QuickBooks)' : 'Check') + '</span>' +
        esc(item.checkNumber) + '</span>';
    } else if (item.invoiceNumber) {
      html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Inv</span>' + esc(item.invoiceNumber) + '</span>';
    }
    if (kind === 'check' && !item.checkNumber && item.checkReference) {
      html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Ref</span>' + esc(item.checkReference) + '</span>';
    }
    if (item.extractedPo) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">PO</span>' + esc(item.extractedPo) + '</span>';
    else if (item.matchKey === 'vendor_order' && item.matchedPo) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">PO (via order)</span>' + esc(item.matchedPo) + '</span>';
    if (item.vendorOrderNumber) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Order</span>' + esc(item.vendorOrderNumber) + '</span>';
    var chipDate = item.checkDate || item.invoiceDate;
    if (chipDate) html += '<span class="ap-inbox-chip">' + esc(chipDate) + '</span>';
    // Spec §7: show sub / tax / total separately — a single $ chip hides which
    // figure the matcher is using (tax-inclusive vs merchandise subtotal)
    var subC = money(item.subtotal);
    var taxC = money(item.tax);
    var totC = money(item.total);
    var tipC = money(item.tip);
    var chargeC = money(item.cardChargeTotal);
    if (subC && totC) {
      html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">sub</span>' + esc(subC) + '</span>';
      if (taxC) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">tax</span>' + esc(taxC) + '</span>';
      html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">total</span>' + esc(totC) + '</span>';
    } else if (totC) {
      html += '<span class="ap-inbox-chip">' + esc(totC) + '</span>';
    }
    // Amendment A3: gratuity receipts have a FOURTH amount — the card charge
    // (total + tip) is what hit the card and what the expense PO carries
    if (tipC) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">tip</span>' + esc(tipC) + '</span>';
    if (chargeC) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">charged</span>' + esc(chargeC) + '</span>';
    if (item.confidence != null) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">AI</span>' + esc(Math.round(item.confidence * 100)) + '%</span>';
    var periodStart = (item.statement && item.statement.periodStart) || item.periodStart;
    var periodEnd = (item.statement && item.statement.periodEnd) || item.periodEnd;
    if (periodStart) {
      html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Period</span>' +
        esc(periodStart) + (periodEnd ? '–' + esc(periodEnd) : '') + '</span>';
    }
    if (item.statementKind) {
      html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Kind</span>' +
        esc(STATEMENT_KIND_LABELS[item.statementKind] || item.statementKind) + '</span>';
    }
    if (state.view !== 'filed_statements') {
      html += '<span class="ap-inbox-chip ap-inbox-chip-reason">' + esc(reasonLabel(item.reviewReason)) + '</span>';
    }
    html += '<button type="button" class="ap-inbox-linkbtn" id="ap-inbox-details-toggle">' +
      (state.showDetails ? 'Hide details' : 'Details') + '</button>';
    html += '</div>';

    if (state.showDetails) {
      html += '<dl class="ap-inbox-meta">';
      html += '<dt>Type</dt><dd>' + esc(item.documentType || '—') + '</dd>';
      html += '<dt>Vendor</dt><dd>' + esc(item.vendor || '—') +
        (item.marketplaceSeller ? ' (seller: ' + esc(item.marketplaceSeller) + ')' : '') + '</dd>';
      html += '<dt>AI PO</dt><dd>' + esc(item.extractedPo || '—') + '</dd>';
      html += '<dt>Invoice #</dt><dd>' + esc(item.invoiceNumber || '—') + '</dd>';
      html += '<dt>Check #</dt><dd>' + esc(item.checkNumber || '—') +
        (item.checkNumber && item.checkNumberSource && CHECK_SOURCE_LABELS[item.checkNumberSource]
          ? ' <span class="ap-inbox-form-hint">(' + esc(CHECK_SOURCE_LABELS[item.checkNumberSource]) + ')</span>' : '') + '</dd>';
      html += '<dt>Reference</dt><dd>' + esc(item.checkReference || '—') + '</dd>';
      if (item.checkNumberLookup && item.checkNumberLookup.label) {
        html += '<dt>QuickBooks lookup</dt><dd>' + esc(item.checkNumberLookup.label) +
          (item.checkNumberLookup.status !== 'recovered' && item.checkNumberLookup.candidates && item.checkNumberLookup.candidates.length
            ? ' — ' + esc(item.checkNumberLookup.candidates.map(function (c) {
              return (c.checkNumber ? '#' + c.checkNumber : 'no #') + (c.totalAmt != null ? ' ' + (money(c.totalAmt) || c.totalAmt) : '') + (c.txnDate ? ' ' + c.txnDate : '');
            }).join(', '))
            : '') + '</dd>';
      }
      html += '<dt>Date</dt><dd>' + esc(item.invoiceDate || '—') + '</dd>';
      html += '<dt>Check date</dt><dd>' + esc(item.checkDate || '—') + '</dd>';
      html += '<dt>Vendor order #</dt><dd>' + esc(item.vendorOrderNumber || '—') + '</dd>';
      html += '<dt>Subtotal</dt><dd>' + esc(money(item.subtotal) || '—') + '</dd>';
      html += '<dt>Tax</dt><dd>' + esc(money(item.tax) || '—') + '</dd>';
      html += '<dt>Total</dt><dd>' + esc(money(item.total) || item.total || '—') + '</dd>';
      html += '<dt>Tip</dt><dd>' + esc(money(item.tip) || '—') + '</dd>';
      html += '<dt>Card charge</dt><dd>' + esc(money(item.cardChargeTotal) || '—') + '</dd>';
      html += '<dt>GL note</dt><dd>' + esc(item.glAnnotation || '—') + '</dd>';
      html += '<dt>Confidence</dt><dd>' + (item.confidence != null ? esc(item.confidence) : '—') + '</dd>';
      html += '<dt>Batch</dt><dd>' + (item.isBatch ? 'Yes' : 'No') + '</dd>';
      html += '<dt>Reason</dt><dd>' + esc(reasonLabel(item.reviewReason)) + '</dd>';
      if (item.closeDate || (item.statement && item.statement.closeDate)) {
        html += '<dt>Close date</dt><dd>' + esc(item.closeDate || item.statement.closeDate) + '</dd>';
      }
      if (item.amountDue != null || (item.statement && item.statement.amountDue != null)) {
        html += '<dt>Amount due</dt><dd>' + esc(money(item.amountDue != null ? item.amountDue : item.statement.amountDue) || '—') + '</dd>';
      }
      html += '</dl>';
      html += renderStatementLinesTable(item);
    }

    if (state.view === 'filed_statements') {
      html += renderFiledStatementDetails(item);
    } else {
      // File as selector — only the chosen form is shown
      html += '<div class="ap-inbox-fileas">';
      html += '<label for="ap-inbox-file-as">File as</label>';
      html += '<select id="ap-inbox-file-as" class="mw_acl-core-form-select">';
      html += '<option value=""' + (state.fileAs === '' ? ' selected' : '') + '>Choose destination…</option>';
      FILE_AS_ORDER.forEach(function(v) {
        html += '<option value="' + v + '"' + (state.fileAs === v ? ' selected' : '') + '>' + esc(FILE_AS_LABELS[v]) + '</option>';
      });
      html += '</select></div>';

      if (state.fileAs === 'po') {
        html += renderPoForm();
      } else if (state.fileAs === 'qbo_link') {
        html += renderQboLinkForm(item);
      } else if (state.fileAs === 'check_stubs' || state.fileAs === 'deposits') {
        html += renderBankingForm(state.fileAs, item);
      } else if (state.fileAs === 'vendor_statement' || state.fileAs === 'bank_statement') {
        html += renderStatementForm(state.fileAs, item);
      }

      html += '<div class="ap-inbox-not-ours">';
      html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-secondary" id="ap-inbox-not-ours-btn"' +
        (isBusy() ? ' disabled' : '') + '>' +
        (state.removing ? 'Removing…' : 'Not ours') + '</button>';
      html += '<p class="ap-inbox-form-hint">Personal or not a MindWorks AP document — keep the scan, leave the queue.</p>';
      html += '</div>';
    }

    paneBody.innerHTML = html;
    bindPaneEvents(item);
  }

  function renderPreviewRegion(item) {
    var region = document.getElementById('ap-inbox-preview-region');
    if (!region) return;

    var html = '';
    html += '<div class="ap-inbox-preview-bar">';
    html += '<span class="ap-inbox-preview-name" title="' + esc(item.fileName || '') + '">' + esc(item.fileName || '') + '</span>';
    html += '<span>';
    if (item.id) {
      html += '<button type="button" class="ap-inbox-linkbtn" id="ap-inbox-inspect" title="Zoom and pan this document in the viewer">Inspect</button> · ';
    }
    if (item.driveLink) {
      html += '<a href="' + esc(item.driveLink) + '" target="_blank" rel="noopener">Open in Drive</a> · ';
    }
    html += '<button type="button" class="ap-inbox-linkbtn" id="ap-inbox-expand">' +
      (state.previewExpanded ? 'Shrink preview' : 'Expand preview') + '</button>';
    html += '</span></div>';

    if (item.previewUrl) {
      html += '<iframe class="ap-inbox-preview' + (state.previewExpanded ? ' ap-inbox-preview-expanded' : '') + '" id="ap-inbox-preview-iframe" src="' + esc(item.previewUrl) + '" title="Document preview"></iframe>';
    }

    region.innerHTML = html;
  }

  function bindPreviewEvents() {
    var inspectBtn = document.getElementById('ap-inbox-inspect');
    if (inspectBtn) {
      inspectBtn.addEventListener('click', function() { handleInspect(selectedItem()); });
    }

    var expandBtn = document.getElementById('ap-inbox-expand');
    if (expandBtn) {
      expandBtn.addEventListener('click', function() {
        state.previewExpanded = !state.previewExpanded;
        var iframe = document.getElementById('ap-inbox-preview-iframe');
        if (iframe) iframe.classList.toggle('ap-inbox-preview-expanded', state.previewExpanded);
        expandBtn.textContent = state.previewExpanded ? 'Shrink preview' : 'Expand preview';
      });
    }
  }

  // === Inspect: open the scan in the site media viewer (zoom/pan) ===
  // The Drive /preview iframe is cross-origin and fits the page to natural size —
  // it cannot be zoomed or panned by us. Inspect pulls the bytes through the API,
  // builds a Blob URL, and hands it to MWACLMediaModal / MWACLPdfModal (url mode).
  function handleInspect(item) {
    var btn = document.getElementById('ap-inbox-inspect');
    if (!item || !btn || btn.disabled) return;

    function open(type, url) {
      if (!window.MWACLMediaModal || !window.MWACLMediaModal.show) {
        toast('Viewer not loaded — use Open in Drive', 'error');
        return;
      }
      window.MWACLMediaModal.show({
        type: type,
        url: url,
        title: item.fileName || 'Document',
        externalUrl: item.driveLink || null
      });
    }

    if (state.inspect && state.inspect.id === item.id) {
      open(state.inspect.type, state.inspect.url);
      return;
    }

    btn.disabled = true;
    var origText = btn.textContent;
    btn.textContent = 'Loading…';

    window.MWACL.API.call('ap_get_inbox_review_file', { docId: item.id })
      .then(function(response) {
        var data = response.data || response;
        if (!data || !data.base64) {
          throw new Error((data && data.error) || (response && response.error) || 'No file returned');
        }
        var bin = atob(data.base64);
        var bytes = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        var mime = data.mimeType || (data.type === 'pdf' ? 'application/pdf' : 'application/octet-stream');
        var url = URL.createObjectURL(new Blob([bytes], { type: mime }));
        if (state.inspect && state.inspect.url) {
          try { URL.revokeObjectURL(state.inspect.url); } catch (e) { /* already gone */ }
        }
        state.inspect = { id: item.id, type: data.type, url: url };
        open(data.type, url);
      })
      .catch(function(err) {
        // 413 / 415 from the API carry a human message that already says "use Open in Drive"
        console.error('[ap-inbox-review] Inspect error:', err);
        toast(err.message || 'Could not load the file', 'error');
      })
      .then(function() {
        btn.disabled = false;
        btn.textContent = origText;
      });
  }

  function aiAdjudicationLine(ai) {
    if (!ai || !ai.verdict) return '';
    var mark = ai.verdict === 'same_purchase' ? '\u2713 same purchase' :
      ai.verdict === 'different' ? '\u2717 different' : '? unsure';
    var cls = ai.verdict === 'same_purchase' ? 'ap-inbox-candidate-ai-yes' : 'ap-inbox-candidate-ai-no';
    var text = 'AI: ' + mark;
    if (ai.reasoning) text += ' \u2014 ' + ai.reasoning;
    return '<div class="ap-inbox-candidate-ai ' + cls + '">' + esc(text) + '</div>';
  }

  function candidateSignalsLine(c) {
    var parts = [];
    (c.matchedSignals || []).forEach(function(s) {
      if (s === 'marketplace_platform' && c.marketplaceIdentity) return;
      if (s === 'vendor_via_remit' && c.remitToName) {
        parts.push('vendor matched via remit-to: ' + c.remitToName);
      } else if (SIGNAL_LABELS[s]) {
        parts.push(SIGNAL_LABELS[s]);
      }
    });
    if (c.taxNote && TAX_NOTE_LABELS[c.taxNote]) parts.push(TAX_NOTE_LABELS[c.taxNote]);
    return parts.join(' \u00b7 ');
  }

  function shipmentCoverageLine(c) {
    var cov = c.shipmentCoverage;
    if (!cov || !cov.shipmentCount) return '';
    var parts = [];
    var m = cov.matched;
    if (m && cov.shipmentCount > 1) {
      parts.push('shipment ' + m.index + ' of ' + m.of);
      if (m.qty != null && m.ofQty) parts.push(m.qty + ' of ' + m.ofQty + ' units');
      else if (m.qty != null) parts.push(m.qty + ' units');
      if (m.amount != null) {
        var basis = BASIS_LABELS[m.basis] || m.basis || 'amount';
        parts.push(basis + ' ' + money(m.amount));
      }
    } else if (cov.shipmentCount > 1) {
      parts.push(cov.shipmentCount + ' shipments');
    }
    if (cov.shipmentCount > 1) {
      parts.push(cov.reconciledCount + ' reconciled, ' + cov.openCount + ' open');
    } else if (c.comparisonBasis && BASIS_LABELS[c.comparisonBasis]) {
      parts.push(BASIS_LABELS[c.comparisonBasis]);
    }
    return parts.join(' \u00b7 ');
  }

  function renderPoForm() {
    var html = '';
    if (state.candidatesLoading) {
      html += '<p>Looking up Fishbowl POs…</p>';
    } else if (state.noMatch) {
      // No-match is a first-class state (spec §7) — say WHY, don't show long-shots
      html += '<div class="ap-inbox-nomatch">';
      html += '<div class="ap-inbox-nomatch-title">No confident PO match</div>';
      html += '<p>' + esc(state.noMatch.message || 'No candidates cleared the confidence floor.') + '</p>';
      html += aiAdjudicationLine(state.noMatch.aiAdjudication);
      html += '<p class="ap-inbox-form-hint">If a PO exists, type its number below. Otherwise this may need a PO created, or it belongs to a non-PO path (File as → Check Stubs / Deposits, or leave for a human).</p>';
      html += '</div>';
    } else if (state.candidates.length === 0) {
      html += '<p class="ap-inbox-form-hint">No nearby Fishbowl POs. Type the PO number below.</p>';
    } else {
      html += '<ul class="ap-inbox-candidates">';
      state.candidates.forEach(function(c) {
        var selected = String(state.chosenPo) === String(c.num) ? ' ap-inbox-candidate-selected' : '';
        var high = c.matchTier === 'high' && !c.annotationConflict ? ' ap-inbox-candidate-high' : '';
        var total = money(c.poTotal);
        var issued = c.dateIssued ? String(c.dateIssued).slice(0, 10) : null;
        var created = c.dateCreated ? String(c.dateCreated).slice(0, 10) : null;

        html += '<li class="ap-inbox-candidate' + selected + high + '" data-po="' + esc(c.num) + '">';
        html += '<div class="ap-inbox-candidate-body">';
        html += '<div class="ap-inbox-candidate-top">';
        html += '<span class="ap-inbox-candidate-num">' + esc(c.num) + '</span>';
        html += '<span class="ap-inbox-candidate-meta">' +
          (total ? esc(total) + ' · ' : '') +
          (issued ? 'Issued ' + esc(issued) : (created ? 'Created ' + esc(created) : '')) +
          (c.vendorName ? ' · ' + esc(c.vendorName) : '') +
          '</span>';
        // Amendment A2: an expense PO's line is a GL account, not merchandise —
        // label it so the human reads the item line as an account, not a product
        if (c.poClass === 'expense') {
          html += '<span class="ap-inbox-candidate-expense">Expense PO</span>';
        }
        if (c.matchTier === 'high' && !c.annotationConflict) {
          html += '<span class="ap-inbox-candidate-tier">Likely match</span>';
        } else if (c.matchTier === 'medium') {
          html += '<span class="ap-inbox-candidate-tier ap-inbox-candidate-tier-med">Possible match \u2014 verify</span>';
        }
        html += '</div>';
        // Spec §7: show WHY it matched — lets a human catch a bad rank instantly
        if (c.matchedVendorSO) {
          html += '<div class="ap-inbox-candidate-signals">Matched on vendor order ' + esc(c.matchedVendorSO) + '</div>';
        } else {
          var sig = candidateSignalsLine(c);
          if (sig) html += '<div class="ap-inbox-candidate-signals">' + esc(sig) + '</div>';
        }
        if (c.marketplaceIdentity) {
          var mi = c.marketplaceIdentity;
          html += '<div class="ap-inbox-candidate-signals">Marketplace: ' + esc(mi.vendor || '') +
            (mi.marketplaceSeller ? ' (seller: ' + esc(mi.marketplaceSeller) + ')' : '') + '</div>';
        }
        var cover = shipmentCoverageLine(c);
        if (cover) {
          html += '<div class="ap-inbox-candidate-coverage">' + esc(cover) + '</div>';
        }
        html += aiAdjudicationLine(c.aiAdjudication);
        if (c.annotationConflict) {
          var warn = c.vendorConflict
            ? '\u26a0 PO read on the document, but the vendor doesn\u2019t match it \u2014 verify before filing'
            : '\u26a0 PO read on the document, but the items don\u2019t match it \u2014 verify before filing';
          html += '<div class="ap-inbox-candidate-warn">' + warn + '</div>';
        }
        if (c.matchedSignals && c.matchedSignals.indexOf('already_reconciled') !== -1) {
          html += '<div class="ap-inbox-candidate-warn">This shipment is already reconciled in Fishbowl \u2014 possible duplicate scan</div>';
        }
        if (c.itemSummary) {
          html += '<div class="ap-inbox-candidate-items" title="' + esc(c.itemSummary) + '">' + esc(c.itemSummary) + '</div>';
        }
        html += '</div></li>';
      });
      html += '</ul>';
    }

    html += '<div class="ap-inbox-file-actions">';
    html += '<input type="text" id="ap-inbox-po" class="mw_acl-core-form-input ap-inbox-po-input" ' +
      'value="' + esc(state.chosenPo) + '" placeholder="PO number" aria-label="PO number">';
    html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-primary" id="ap-inbox-file-btn"' +
      (isBusy() ? ' disabled' : '') + '>' + (state.filing ? 'Filing…' : 'File to PO') + '</button>';
    html += '</div>';
    return html;
  }

  function renderBankingForm(destination, item) {
    var isStubs = destination === 'check_stubs';
    var html = '';
    html += '<p class="ap-inbox-form-hint">' +
      (isStubs ? 'Vendor remittance we mailed → Accounting/Banking/Check Stubs.' :
        'Customer paid us → Accounting/Banking/Deposits (one check per PDF).') + '</p>';
    // v1.34: say where the prefilled check number came from, or why QuickBooks
    // could not supply one — the reviewer decides with the evidence in view.
    if (isStubs && item && item.checkNumberLookup && item.checkNumberLookup.label) {
      var recovered = item.checkNumberLookup.status === 'recovered' && item.checkNumberSource === 'qbo_bill_payment';
      html += '<p class="ap-inbox-form-hint">' + esc(item.checkNumberLookup.label) +
        (recovered && item.checkReference ? ' — paid vendor invoice ' + esc(item.checkReference) + '. Confirm and file.' : '') +
        (!recovered && item.checkReference ? ' (reference ' + esc(item.checkReference) + '). Enter the check number from the stub.' : '') +
        '</p>';
    }
    html += '<div class="ap-inbox-banking-fields">';
    html += '<label for="ap-inbox-check">Check #</label>';
    html += '<input type="text" id="ap-inbox-check" class="mw_acl-core-form-input" value="' +
      esc(state.checkNumber) + '" placeholder="8308" ' + (state.isBatch ? 'disabled' : '') + '>';
    html += '<label for="ap-inbox-payee">' + (isStubs ? 'Payee' : 'Customer') + '</label>';
    html += '<input type="text" id="ap-inbox-payee" class="mw_acl-core-form-input" value="' +
      esc(state.payee) + '" placeholder="' + (isStubs ? 'Vendor' : 'Customer') + '">';
    html += '<label for="ap-inbox-ref">Reference</label>';
    html += '<input type="text" id="ap-inbox-ref" class="mw_acl-core-form-input" value="' +
      esc(state.reference) + '" placeholder="Invoice / remittance #">';
    html += '<label for="ap-inbox-check-date">Check date</label>';
    html += '<input type="text" id="ap-inbox-check-date" class="mw_acl-core-form-input" value="' +
      esc(state.checkDate) + '" placeholder="YYYY-MM-DD">';
    if (isStubs) {
      html += '<label for="ap-inbox-batch">Batch PDF</label>';
      html += '<input type="checkbox" id="ap-inbox-batch"' + (state.isBatch ? ' checked' : '') + '>';
    }
    html += '</div>';
    html += '<div class="ap-inbox-file-actions">';
    html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-primary" id="ap-inbox-banking-btn"' +
      (isBusy() ? ' disabled' : '') + '>' +
      (state.filing ? 'Filing…' : (isStubs ? 'File to Check Stubs' : 'File to Deposits')) + '</button>';
    html += '</div>';
    return html;
  }

  // Q2: bill already in QBO — find it, file the PDF, stamp the Drive link
  function renderQboLinkForm(item) {
    var half = item.reviewReason === HALF_STATE_REASON;
    var html = '';
    html += '<p class="ap-inbox-form-hint">Bill is already in QBO. Find it, file the PDF to ' +
      'Accounting/Vendors/{Vendor}/{year}, stamp the Drive link on the bill.</p>';
    if (half) {
      html += '<p class="ap-inbox-form-hint ap-inbox-qbo-half">PDF already filed' +
        (item.destinationPath ? ' to Accounting/' + esc(item.destinationPath) : '') +
        ' — retry stamps QBO only.</p>';
    }
    html += '<div class="ap-inbox-form-fields">';
    html += '<label for="ap-inbox-qbo-vendor">Vendor</label>';
    html += '<input type="text" id="ap-inbox-qbo-vendor" class="mw_acl-core-form-input" value="' +
      esc(state.qbo.vendorName) + '" placeholder="QBO vendor name">';
    html += '<label for="ap-inbox-qbo-vendor-id">Vendor Id</label>';
    html += '<input type="text" id="ap-inbox-qbo-vendor-id" class="mw_acl-core-form-input" value="' +
      esc(state.qbo.vendorId) + '" placeholder="optional — QBO vendor Id, wins over name (e.g. 2669)">';
    html += '<label for="ap-inbox-qbo-doc">Invoice #</label>';
    html += '<input type="text" id="ap-inbox-qbo-doc" class="mw_acl-core-form-input" value="' +
      esc(state.qbo.docNumber) + '" placeholder="optional — empty for bills with no Bill no.">';
    html += '<label for="ap-inbox-qbo-date">Bill date</label>';
    html += '<input type="text" id="ap-inbox-qbo-date" class="mw_acl-core-form-input" value="' +
      esc(state.qbo.txnDate) + '" placeholder="YYYY-MM-DD">';
    html += '<label for="ap-inbox-qbo-total">Amount</label>';
    html += '<input type="text" id="ap-inbox-qbo-total" class="mw_acl-core-form-input" value="' +
      esc(state.qbo.total) + '" placeholder="108.25">';
    html += '</div>';
    html += '<div class="ap-inbox-file-actions">';
    html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-primary" id="ap-inbox-qbo-find"' +
      (isBusy() || state.qboFinding ? ' disabled' : '') + '>' +
      (state.qboFinding ? 'Finding…' : 'Find QBO bill') + '</button>';
    html += '</div>';
    return html;
  }

  function renderStatementLinesTable(item) {
    var lines = (item.statement && item.statement.lines) || [];
    if (!lines.length) return '';
    var html = '<div class="mw_acl-core-table-container ap-inbox-stmt-lines"><table class="mw_acl-core-table">';
    html += '<thead><tr><th>Kind</th><th>Number</th><th>Date</th><th>Amount</th><th>PO</th></tr></thead><tbody>';
    lines.forEach(function(line) {
      html += '<tr><td>' + esc(line.kind || '—') + '</td><td>' + esc(line.number || '—') + '</td><td>' +
        esc(line.date || '—') + '</td><td>' + esc(money(line.amount) || line.amount || '—') + '</td><td>' +
        esc(line.poNumber || '—') + '</td></tr>';
    });
    html += '</tbody></table></div>';
    return html;
  }

  function coverageCheckedDay(iso) {
    if (!iso) return '';
    var m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
  }

  function coverageEvidenceBits(finding) {
    var ev = finding.evidence || {};
    var bits = [];
    if (finding.docNumber) bits.push(esc(finding.docNumber));
    if (finding.amount != null) bits.push(esc(money(finding.amount) || finding.amount));
    if (ev.statementAmount != null) bits.push('statement ' + esc(money(ev.statementAmount) || ev.statementAmount));
    if (ev.qbo && ev.qbo.TotalAmt != null) bits.push('QuickBooks ' + esc(money(ev.qbo.TotalAmt) || ev.qbo.TotalAmt));
    if (ev.qbo && ev.qbo.DueDate) bits.push('due ' + esc(ev.qbo.DueDate));
    if (ev.qbo && ev.qbo.TxnDate) bits.push('dated ' + esc(ev.qbo.TxnDate));
    if (ev.payment && ev.payment.TxnDate) bits.push('paid ' + esc(ev.payment.TxnDate));
    if (ev.fishbowl && ev.fishbowl.poNumber) bits.push('PO ' + esc(ev.fishbowl.poNumber));
    if (ev.fishbowl && ev.fishbowl.poNum) bits.push('PO ' + esc(ev.fishbowl.poNum));
    if (ev.fishbowl && ev.fishbowl.poStatusId != null) {
      var poStatusLabel = PO_STATUS_LABELS[ev.fishbowl.poStatusId] || String(ev.fishbowl.poStatusId);
      bits.push('PO status ' + poStatusLabel);
    }
    if (ev.fishbowl && ev.fishbowl.unreconciledItems > 0) {
      bits.push(ev.fishbowl.unreconciledItems + ' item(s) not reconciled');
    }
    if (ev.qbo && ev.qbo.openAtClose != null) bits.push('QuickBooks at close ' + esc(money(ev.qbo.openAtClose) || ev.qbo.openAtClose));
    return bits.join(' · ');
  }

  function fixingDoorHtml(code, finding) {
    if (code === 'missing_bill') {
      var reason = finding && finding.reason;
      var po = finding && finding.evidence && finding.evidence.fishbowl && finding.evidence.fishbowl.poNum;
      var inbox = finding && finding.evidence && finding.evidence.inbox;
      if (reason === 'po_partially_received') {
        return 'Receive the balance of PO ' + esc(po) + ' in Fishbowl, or close it short, then reconcile — ask the vendor for the rest of the shipment';
      }
      if (reason === 'po_not_received') {
        return 'Nothing received on PO ' + esc(po) + ' yet — check receiving / drop-ship before paying';
      }
      if (reason === 'received_awaiting_reconcile') {
        if (inbox && inbox.docId) {
          return '<a href="/pages/acl-ap-reconciliation-detail?docId=' + encodeURIComponent(inbox.docId) + '">Reconcile PO ' + esc(po) + ' now</a>';
        }
        return '<a href="/pages/acl-ap-reconciliation">Open PO Reconciliation</a> — request the invoice if we do not have it';
      }
      if (reason === 'reconciled_not_synced') {
        return 'Post the bill from Fishbowl to QuickBooks';
      }
      if (reason === 'invoice_in_review') {
        if (inbox && inbox.status === 'needs_review') {
          return 'In Inbox Review — file it first (Review tab)';
        }
        if (inbox && inbox.docId) {
          return '<a href="/pages/acl-ap-reconciliation-detail?docId=' + encodeURIComponent(inbox.docId) + '">Open in reconciliation</a>';
        }
        return 'In Inbox Review — file it first (Review tab)';
      }
      return 'Request the invoice from the vendor, then enter the bill';
    }
    if (code === 'missing_po') return '<a href="/pages/acl-ap-reconciliation">Open PO Reconciliation</a>';
    if (code === 'past_due_unpaid' || code === 'payment_not_credited') return 'Pay / confirm payment in QuickBooks';
    if (code === 'credit_not_entered') return 'Enter the credit memo in QuickBooks';
    if (code === 'amount_mismatch' || code === 'balance_mismatch') return 'Compare with the vendor';
    return '';
  }

  function renderCoveragePanel(item) {
    var cov = item.statementCoverage;
    if (!cov) return '';
    var html = '<section class="ap-inbox-coverage">';
    var day = coverageCheckedDay(cov.checkedAt || item.statementCheckedAt);
    html += '<h3 class="ap-inbox-coverage-title">Coverage check' + (day ? ' — ' + esc(day) : '') + '</h3>';
    if (cov.risk === 'credit_hold') {
      html += '<p><span class="mw_acl-core-badge mw_acl-core-badge-failed">Credit-hold risk</span></p>';
    }
    if (cov.error) {
      html += '<p><span class="mw_acl-core-badge mw_acl-core-badge-failed">Check failed — ' + esc(cov.error) + '</span></p>';
      html += '</section>';
      return html;
    }
    var findings = Array.isArray(cov.findings) ? cov.findings : [];
    var notes = Array.isArray(cov.notes) ? cov.notes : [];
    var uneval = Array.isArray(cov.unevaluated) ? cov.unevaluated : [];
    if (cov.verdict === 'clear') {
      html += '<p class="ap-inbox-coverage-verdict">Clear</p>';
    } else {
      html += '<p class="ap-inbox-coverage-verdict">' + findings.length + ' finding' + (findings.length === 1 ? '' : 's') + '</p>';
    }
    if (findings.length) {
      html += '<ul class="ap-inbox-coverage-list">';
      findings.forEach(function(f) {
        var door = fixingDoorHtml(f.code, f);
        var findingLabel = FINDING_LABELS[f.code] || f.code;
        if (f.code === 'missing_bill' && f.reason && MISSING_BILL_REASON_LABELS[f.reason]) {
          findingLabel += ' · ' + MISSING_BILL_REASON_LABELS[f.reason];
        }
        html += '<li><strong>Line ' + esc(f.lineNumber != null ? f.lineNumber : '—') + '</strong> — ' +
          esc(findingLabel);
        var ev = coverageEvidenceBits(f);
        if (ev) html += '<div class="ap-inbox-coverage-evidence">' + ev + '</div>';
        if (door) html += '<div class="ap-inbox-coverage-door">' + door + '</div>';
        html += '</li>';
      });
      html += '</ul>';
    }
    if (notes.length) {
      html += '<details class="ap-inbox-coverage-notes"><summary>Notes (' + notes.length + ')</summary><ul class="ap-inbox-coverage-list">';
      notes.forEach(function(n) {
        html += '<li><strong>Line ' + esc(n.lineNumber != null ? n.lineNumber : '—') + '</strong> — ' +
          esc(NOTE_LABELS[n.code] || n.code);
        var ev = coverageEvidenceBits(n);
        if (ev) html += '<div class="ap-inbox-coverage-evidence">' + ev + '</div>';
        html += '</li>';
      });
      html += '</ul></details>';
    }
    if (uneval.length) {
      html += '<p class="ap-inbox-coverage-uneval">Not evaluated (' + uneval.length + ')</p>';
    }
    if (cov.balance) {
      var bal = cov.balance;
      var tol = cov.tolerances && cov.tolerances.BALANCE_TOLERANCE != null ? cov.tolerances.BALANCE_TOLERANCE : 1;
      var signed = function(v) { return v == null ? null : (v < 0 ? '\u2212' : '') + money(Math.abs(v)); };
      html += '<p class="ap-inbox-coverage-balance">Balance at close — statement ' +
        esc(money(bal.statement) || '—') + ' vs QuickBooks ' +
        esc(money(bal.qboOpenAtClose) || '—') +
        (bal.explainedByNotes ? ' + ' + esc(money(bal.explainedByNotes)) + ' in transit' : '') +
        (bal.delta != null ? ' (off by ' + esc(signed(bal.delta)) + ')' : '') +
        ', tolerance ' + esc(money(tol) || tol) + '</p>';
    }
    html += '</section>';
    return html;
  }

  function renderFiledStatementDetails(item) {
    var st = item.statement || {};
    var html = '<dl class="ap-inbox-meta">';
    html += '<dt>Period</dt><dd>' + esc((st.periodStart || item.periodStart || '—') +
      ((st.periodEnd || item.periodEnd) ? ' – ' + (st.periodEnd || item.periodEnd) : '')) + '</dd>';
    html += '<dt>Close date</dt><dd>' + esc(item.closeDate || st.closeDate || '—') + '</dd>';
    html += '<dt>Opening</dt><dd>' + esc(money(st.openingBalance) || '—') + '</dd>';
    html += '<dt>Closing</dt><dd>' + esc(money(item.closingBalance != null ? item.closingBalance : st.closingBalance) || '—') + '</dd>';
    html += '<dt>Amount due</dt><dd>' + esc(money(item.amountDue != null ? item.amountDue : st.amountDue) || '—') + '</dd>';
    html += '<dt>Filed to</dt><dd>' + esc(item.destinationPath || '—') + '</dd>';
    html += '</dl>';
    html += renderStatementLinesTable(item);
    html += renderCoveragePanel(item);
    return html;
  }

  function renderFolderCandidates(candidates, selectedId, radioName) {
    var html = '<div class="ap-inbox-qbo-candidates">';
    (candidates || []).forEach(function(f) {
      var id = f.folderId || f.id || '';
      var checked = selectedId === id;
      html += '<label class="ap-inbox-qbo-candidate' + (checked ? ' ap-inbox-qbo-candidate-selected' : '') + '">';
      html += '<input type="radio" name="' + esc(radioName) + '" value="' + esc(id) + '"' + (checked ? ' checked' : '') + '>';
      html += '<span class="ap-inbox-qbo-candidate-text">' + esc(f.name || id) +
        (id ? ' <span class="ap-inbox-qbo-tag">' + esc(id) + '</span>' : '') + '</span>';
      html += '</label>';
    });
    html += '</div>';
    return html;
  }

  function renderQboVendorPicker(vs, selected) {
    vs = vs || {};
    var html = '<label for="ap-inbox-qbo-vendor-search">QuickBooks vendor</label>';
    html += '<div class="ap-inbox-qbo-vendor-picker">';

    var selectedId = selected && selected.id;
    if (selectedId) {
      html += '<div class="ap-inbox-qbo-vendor-selected">';
      html += '<span class="mw_acl-core-badge mw_acl-core-badge-active">\u2713</span> ';
      html += esc(selected.displayName);
      html += ' <button type="button" class="mw_acl-core-btn mw_acl-core-btn-sm mw_acl-core-btn-secondary" id="ap-inbox-qbo-vendor-change">Change</button>';
      html += '</div>';
      // Hint only when the selection IS the server's loose auto-pick (not after Change → manual pick).
      var first = (vs.candidates && vs.candidates[0]) || null;
      if (vs.confident && String(vs.confident.id) === String(selectedId) && first && first.match === 'loose') {
        html += '<p class="ap-inbox-form-hint">Matched by name \u2014 statement says \u201c' +
          esc(vs.searchedFor) + '\u201d.</p>';
      }
    } else if (vs.loading) {
      html += '<p class="ap-inbox-form-hint">Searching QuickBooks\u2026</p>';
    } else {
      if (vs.error) {
        html += '<p><span class="mw_acl-core-badge mw_acl-core-badge-failed">Search failed \u2014 ' +
          esc(vs.error) + '</span></p>';
      }
      var list = vs.candidates;
      if (list && list.length) {
        html += '<div class="ap-inbox-qbo-candidates">';
        list.forEach(function(c, idx) {
          var inactive = c.active === false;
          html += '<label class="ap-inbox-qbo-candidate' +
            (inactive ? ' ap-inbox-qbo-candidate-disabled' : '') + '">';
          html += '<input type="radio" name="ap-inbox-qbo-vendor-pick" value="' +
            esc(String(idx)) + '"' + (inactive ? ' disabled' : '') + '>';
          html += '<span class="ap-inbox-qbo-candidate-text">' + esc(c.displayName || '');
          if (inactive) {
            html += ' <span class="mw_acl-core-badge mw_acl-core-badge-neutral">inactive</span>';
          }
          html += '</span></label>';
        });
        html += '</div>';
      } else if (list && !vs.error) {
        html += '<p class="ap-inbox-form-hint">No QuickBooks vendor matches \u201c' +
          esc(vs.searchedFor || vs.query) + '\u201d. Try a shorter name.</p>';
      }
    }

    html += '<div class="ap-inbox-qbo-vendor-search">';
    html += '<input type="text" id="ap-inbox-qbo-vendor-search" class="mw_acl-core-form-input" value="' +
      esc(vs.query || '') + '" placeholder="Search QuickBooks vendors\u2026">';
    html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-sm mw_acl-core-btn-secondary" id="ap-inbox-qbo-vendor-search-btn">Search</button>';
    html += '</div>';

    html += '</div>';
    return html;
  }

  function renderStatementForm(destination, item) {
    var isVendor = destination === 'vendor_statement';
    var s = state.stmt;
    var html = '';
    if (isVendor) {
      html += '<p class="ap-inbox-form-hint">A statement is not a bill — filed to Accounting/Vendors/{Vendor}/{year} only; nothing is created in QBO. Filing teaches the vendor alias so the next statement from this vendor files itself.</p>';
    } else {
      html += '<p class="ap-inbox-form-hint">Our account statement → Accounting/Banking/{Account}/{year}. Pick the account folder.</p>';
    }
    html += '<div class="ap-inbox-chips">';
    var st = item.statement || {};
    var pStart = st.periodStart || item.periodStart;
    var pEnd = st.periodEnd || item.periodEnd;
    if (pStart || pEnd) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Period</span>' + esc((pStart || '') + (pEnd ? '–' + pEnd : '')) + '</span>';
    var due = item.statementAmountDue != null ? item.statementAmountDue : (item.amountDue != null ? item.amountDue : st.amountDue);
    if (due != null) html += '<span class="ap-inbox-chip"><span class="ap-inbox-chip-label">Due</span>' + esc(money(due) || due) + '</span>';
    var nLines = (st.lines && st.lines.length) || item.lineCount || 0;
    if (nLines) html += '<span class="ap-inbox-chip">' + nLines + ' lines</span>';
    if (st.attachedInvoiceCopies || item.attachedInvoiceCopies) html += '<span class="ap-inbox-chip">with invoice copies</span>';
    html += '</div>';

    html += '<div class="ap-inbox-form-fields">';
    if (isVendor) {
      html += '<label>Vendor</label><input type="text" class="mw_acl-core-form-input" value="' + esc(item.vendor || '') + '" readonly>';
      var vs = s.vendorSearch;
      // Auto-search once per pane open: candidates stay null until the first fetch
      // finishes (or errors to []), so later renderPane() calls do not re-fire.
      if (vs.candidates === null && !vs.loading && String(vs.query || '').trim().length >= 2) {
        searchQboVendors(vs.query);
      }
      html += renderQboVendorPicker(vs, { id: s.vendorId, displayName: s.vendorDisplayName });
    } else {
      html += '<label for="ap-inbox-stmt-account">Account</label>';
      if (state.bankingAccountsLoading || !state.bankingAccounts) {
        html += '<span>Loading accounts…</span>';
        ensureBankingAccounts();
      } else {
        html += '<select id="ap-inbox-stmt-account" class="mw_acl-core-form-select">';
        html += '<option value="">Choose account…</option>';
        state.bankingAccounts.forEach(function(a) {
          html += '<option value="' + esc(a.folderId) + '"' + (s.accountFolderId === a.folderId ? ' selected' : '') + '>' + esc(a.name) + '</option>';
        });
        html += '</select>';
      }
      html += '<label for="ap-inbox-stmt-kind">Kind</label>';
      html += '<select id="ap-inbox-stmt-kind" class="mw_acl-core-form-select">';
      ['bank', 'card', 'loc'].forEach(function(k) {
        html += '<option value="' + k + '"' + (s.kind === k ? ' selected' : '') + '>' + esc(STATEMENT_KIND_LABELS[k]) + '</option>';
      });
      html += '</select>';
    }
    html += '<label for="ap-inbox-stmt-close">Close date</label>';
    html += '<input type="text" id="ap-inbox-stmt-close" class="mw_acl-core-form-input" value="' + esc(s.closeDate) + '" placeholder="YYYY-MM-DD">';
    html += '</div>';

    if (isVendor && s.filing && s.filing.candidates && s.filing.candidates.length) {
      html += '<p class="ap-inbox-form-hint">' + esc(s.filing.error || 'Confirm a folder') + '</p>';
      html += renderFolderCandidates(s.filing.candidates, s.folderId, 'ap-inbox-stmt-folder');
      if (s.filing.candidateLevel === 'entity') {
        var exact = (s.filing.candidates || []).some(function(c) {
          return c.name && s.vendorDisplayName && String(c.name).toLowerCase() === String(s.vendorDisplayName).toLowerCase();
        });
        if (!exact) {
          html += '<label class="ap-inbox-stmt-force"><input type="checkbox" id="ap-inbox-stmt-force"' + (s.forceCreate ? ' checked' : '') + '> This is a new vendor — create its folder</label>';
        }
      }
    }

    var stmtReady = !isVendor || !!(s.vendorId && s.vendorDisplayName);
    var stmtLabel = state.filing ? 'Filing…' :
      (!stmtReady ? 'Pick the QuickBooks vendor' :
        (isVendor ? 'File to Vendors' : 'File to Banking'));
    html += '<div class="ap-inbox-file-actions">';
    html += '<button type="button" class="mw_acl-core-btn mw_acl-core-btn-primary" id="ap-inbox-stmt-btn"' +
      (isBusy() || !stmtReady ? ' disabled' : '') + '>' + stmtLabel + '</button>';
    html += '</div>';
    return html;
  }

  function ensureBankingAccounts() {
    if (state.bankingAccounts || state.bankingAccountsLoading) return;
    state.bankingAccountsLoading = true;
    window.MWACL.API.call('ap_list_banking_accounts', {})
      .then(function(response) {
        var data = response.data || response;
        state.bankingAccounts = data.accounts || [];
        state.bankingAccountsLoading = false;
        if (state.fileAs === 'bank_statement') renderPane();
      })
      .catch(function(err) {
        state.bankingAccountsLoading = false;
        state.bankingAccounts = [];
        console.error('[ap-inbox-review] Banking accounts error:', err);
        toast(err.message || 'Could not load bank accounts', 'error');
        if (state.fileAs === 'bank_statement') renderPane();
      });
  }

  function searchQboVendors(query, clearSelection) {
    var stmt = state.stmt; // the item this search belongs to — a later pane open replaces state.stmt
    var vs = stmt.vendorSearch;
    if (!vs || vs.loading) return;
    var name = String(query || '').trim();
    if (name.length < 2) {
      toast('Type at least 2 characters', 'warning');
      return;
    }
    vs.query = name;
    vs.loading = true;
    vs.error = null;
    if (clearSelection) {
      stmt.vendorId = '';
      stmt.vendorDisplayName = '';
    }
    var stillCurrent = function() { return state.stmt === stmt && state.fileAs === 'vendor_statement'; };
    window.MWACL.API.call('ap_search_qbo_vendors', { name: name })
      .then(function(response) {
        var data = response.data || response;
        vs.searchedFor = data.searchedFor || name;
        vs.candidates = data.candidates || [];
        vs.confident = data.confident || null;
        vs.loading = false;
        if (vs.confident) {
          stmt.vendorId = vs.confident.id;
          stmt.vendorDisplayName = vs.confident.displayName;
        }
        if (stillCurrent()) renderPane();
      })
      .catch(function(err) {
        vs.loading = false;
        vs.candidates = [];
        vs.confident = null;
        vs.error = (err && err.message) || 'Search failed';
        console.error('[ap-inbox-review] QBO vendor search error:', err);
        if (stillCurrent()) {
          toast(vs.error, 'error');
          renderPane();
        }
      });
  }

  function bindPaneEvents(item) {
    var pane = document.getElementById('ap-inbox-pane');
    if (!pane) return;

    var detailsBtn = document.getElementById('ap-inbox-details-toggle');
    if (detailsBtn) {
      detailsBtn.addEventListener('click', function() {
        state.showDetails = !state.showDetails;
        renderPane();
      });
    }

    var fileAsSelect = document.getElementById('ap-inbox-file-as');
    if (fileAsSelect) {
      fileAsSelect.addEventListener('change', function(e) {
        state.fileAs = e.target.value;
        renderPane();
        if (state.fileAs === 'po') loadCandidates(item.id);
      });
    }

    pane.querySelectorAll('.ap-inbox-candidate').forEach(function(el) {
      el.addEventListener('click', function() {
        state.chosenPo = el.getAttribute('data-po') || '';
        renderPane();
      });
    });

    var poInput = document.getElementById('ap-inbox-po');
    if (poInput) {
      poInput.addEventListener('input', function(e) {
        state.chosenPo = e.target.value.trim();
      });
      poInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') { e.preventDefault(); handleFile(); }
      });
    }

    var fileBtn = document.getElementById('ap-inbox-file-btn');
    if (fileBtn) fileBtn.addEventListener('click', handleFile);

    function bindField(id, key) {
      var el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', function(e) {
        state[key] = e.target.value.trim();
      });
    }
    bindField('ap-inbox-check', 'checkNumber');
    bindField('ap-inbox-payee', 'payee');
    bindField('ap-inbox-ref', 'reference');
    bindField('ap-inbox-check-date', 'checkDate');

    var batchEl = document.getElementById('ap-inbox-batch');
    if (batchEl) {
      batchEl.addEventListener('change', function(e) {
        state.isBatch = !!e.target.checked;
        renderPane();
      });
    }

    var bankingBtn = document.getElementById('ap-inbox-banking-btn');
    if (bankingBtn) bankingBtn.addEventListener('click', function() { handleFileBanking(state.fileAs); });

    // qbo_link form: fields → state.qbo, Enter → Find
    function bindQboField(id, key) {
      var el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', function(e) { state.qbo[key] = e.target.value.trim(); });
      el.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') { e.preventDefault(); handleQboLinkPreview(); }
      });
    }
    bindQboField('ap-inbox-qbo-vendor', 'vendorName');
    bindQboField('ap-inbox-qbo-vendor-id', 'vendorId');
    bindQboField('ap-inbox-qbo-doc', 'docNumber');
    bindQboField('ap-inbox-qbo-date', 'txnDate');
    bindQboField('ap-inbox-qbo-total', 'total');
    var qboFindBtn = document.getElementById('ap-inbox-qbo-find');
    if (qboFindBtn) qboFindBtn.addEventListener('click', handleQboLinkPreview);

    function bindStmtField(id, key) {
      var el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', function(e) { state.stmt[key] = e.target.value.trim(); });
      el.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') { e.preventDefault(); handleFileStatement(state.fileAs); }
      });
    }
    bindStmtField('ap-inbox-stmt-close', 'closeDate');

    pane.querySelectorAll('input[name="ap-inbox-qbo-vendor-pick"]').forEach(function(r) {
      r.addEventListener('change', function() {
        var idx = parseInt(r.value, 10);
        var list = (state.stmt.vendorSearch && state.stmt.vendorSearch.candidates) || [];
        var c = list[idx];
        if (!c || c.active === false) return;
        state.stmt.vendorId = c.id;
        state.stmt.vendorDisplayName = c.displayName;
        renderPane();
      });
    });
    var vendorChangeBtn = pane.querySelector('#ap-inbox-qbo-vendor-change');
    if (vendorChangeBtn) {
      vendorChangeBtn.addEventListener('click', function() {
        state.stmt.vendorId = '';
        state.stmt.vendorDisplayName = '';
        renderPane();
      });
    }
    var vendorSearchInput = pane.querySelector('#ap-inbox-qbo-vendor-search');
    var vendorSearchBtn = pane.querySelector('#ap-inbox-qbo-vendor-search-btn');
    function runVendorSearch() {
      var q = vendorSearchInput ? vendorSearchInput.value : '';
      if (vendorSearchInput) state.stmt.vendorSearch.query = vendorSearchInput.value;
      searchQboVendors(q, true);
      if (state.stmt.vendorSearch.loading) renderPane();
    }
    if (vendorSearchBtn) vendorSearchBtn.addEventListener('click', runVendorSearch);
    if (vendorSearchInput) {
      vendorSearchInput.addEventListener('input', function(e) {
        state.stmt.vendorSearch.query = e.target.value;
      });
      vendorSearchInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') { e.preventDefault(); runVendorSearch(); }
      });
    }
    var acctSel = document.getElementById('ap-inbox-stmt-account');
    if (acctSel) {
      acctSel.addEventListener('change', function(e) {
        state.stmt.accountFolderId = e.target.value;
        var opt = e.target.options[e.target.selectedIndex];
        state.stmt.accountName = opt ? opt.textContent : '';
      });
    }
    var kindSel = document.getElementById('ap-inbox-stmt-kind');
    if (kindSel) {
      kindSel.addEventListener('change', function(e) { state.stmt.kind = e.target.value; });
    }
    pane.querySelectorAll('input[name="ap-inbox-stmt-folder"]').forEach(function(r) {
      r.addEventListener('change', function() { state.stmt.folderId = r.value; renderPane(); });
    });
    var forceEl = document.getElementById('ap-inbox-stmt-force');
    if (forceEl) {
      forceEl.addEventListener('change', function(e) { state.stmt.forceCreate = !!e.target.checked; });
    }
    var stmtBtn = document.getElementById('ap-inbox-stmt-btn');
    if (stmtBtn) stmtBtn.addEventListener('click', function() { handleFileStatement(state.fileAs); });

    var notOursBtn = document.getElementById('ap-inbox-not-ours-btn');
    if (notOursBtn) notOursBtn.addEventListener('click', confirmNotOurs);
  }

  // === Q2: qbo_link — preview → confirm modal → link & file ===

  // One payload builder for preview AND apply (T2T): vendor_id wins over vendor_name.
  // doc_number is ALWAYS sent — an omitted key makes the Admin API fall back to the
  // classifier's invoiceNumber (buildLinkBody `body.doc_number ?? doc.invoiceNumber`),
  // so clearing Invoice # in the form must reach the server as an explicit ''.
  function qboLinkPayload(item) {
    var q = state.qbo;
    var payload = { docId: item.id, txn_date: q.txnDate, total: parseFloat(q.total), doc_number: q.docNumber || '' };
    if (q.vendorId) payload.vendor_id = q.vendorId;
    else payload.vendor_name = q.vendorName;
    return payload;
  }

  function validateQboForm() {
    var q = state.qbo;
    if (!q.vendorId && !q.vendorName) return 'Enter the vendor name or QBO vendor Id';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(q.txnDate)) return 'Bill date must be YYYY-MM-DD';
    var total = parseFloat(q.total);
    if (!(total > 0)) return 'Enter the bill amount';
    return null;
  }

  function handleQboLinkPreview() {
    var item = selectedItem();
    if (!item || isBusy() || state.qboFinding) return;
    var problem = validateQboForm();
    if (problem) { toast(problem, 'warning'); return; }

    state.qboFinding = true;
    renderPane();

    var payload = qboLinkPayload(item);
    payload.apply = false;
    window.MWACL.API.call('ap_link_qbo_entry', payload)
      .then(function(response) {
        state.qboFinding = false;
        if (state.selectedId !== item.id) return; // user moved on
        var data = response.data || response;
        state.qboPreview = data;
        state.qboFolderId = '';
        // Exactly one enabled candidate → pre-selected; 2+ → none
        var enabled = (data.candidates || []).filter(function(c) { return !c.otherDriveUrl; });
        state.qboSelectedTxnId = enabled.length === 1 ? String(enabled[0].txnId) : '';
        state.qboFileTo = defaultQboFileTo();
        renderPane();
        openQboLinkModal(item);
      })
      .catch(function(err) {
        state.qboFinding = false;
        renderPane();
        console.error('[ap-inbox-review] QBO find error:', err);
        toast(err.message || 'Could not find the QBO bill', 'error');
      });
  }

  function displayPath(path) {
    return path ? 'Accounting/' + path : '';
  }

  function qboSelectedCandidate() {
    var data = state.qboPreview;
    if (!data) return null;
    return (data.candidates || []).find(function(c) { return String(c.txnId) === state.qboSelectedTxnId; }) || null;
  }

  // The chosen bill's memo names a Fishbowl PO the Admin API VERIFIED (family exists,
  // not voided, vendor overlaps). Only then is the PO folder offered.
  function qboVerifiedPo() {
    var chosen = qboSelectedCandidate();
    var fp = chosen && chosen.fishbowlPo;
    return fp && fp.verified ? fp : null;
  }

  // §4 boundary rule: a Fishbowl PO exists → Fishbowl owns the record → PO folder is
  // the default. Retry keeps whatever mode the half state was filed in (data.fileTo).
  function defaultQboFileTo() {
    var data = state.qboPreview || {};
    if (data.retry) return data.fileTo === 'po' ? 'po' : 'vendor';
    return qboVerifiedPo() ? 'po' : 'vendor';
  }

  function qboPoMode() {
    return state.qboFileTo === 'po' && (!!qboVerifiedPo() || !!(state.qboPreview && state.qboPreview.retry));
  }

  function qboLinkReady() {
    var data = state.qboPreview;
    if (!data) return false;
    var chosen = qboSelectedCandidate();
    if (!chosen || chosen.otherDriveUrl) return false;
    if (qboPoMode()) return true; // PO folder needs no vendor-folder pick
    var filing = data.filing || {};
    if (filing.status === 'refuse' && !state.qboFolderId) return false;
    return true;
  }

  // Vendor-folder picker when the entity folder map refused (used alone, or under the PO/vendor choice)
  function renderQboFolderPicker(filing) {
    var html = renderFolderCandidates(filing.candidates || [], state.qboFolderId, 'ap-inbox-qbo-folder');
    if (filing.fileName) html += '<p class="ap-inbox-form-hint">as ' + esc(filing.fileName) + '</p>';
    return html;
  }

  function renderQboModalBody(item) {
    var data = state.qboPreview || {};
    var vendor = data.vendor || {};
    var candidates = data.candidates || [];
    var nearby = data.nearby || [];
    var filing = data.filing || {};
    var errors = data.errors || [];
    var warnings = data.warnings || [];
    var html = '';

    html += '<p class="ap-inbox-qbo-vendor">QBO vendor: <strong>' + esc(vendor.displayName || state.qbo.vendorName || '—') +
      '</strong>' + (vendor.id ? ' (' + esc(vendor.id) + ')' : '') + '</p>';

    if (candidates.length) {
      html += '<div class="ap-inbox-qbo-section-title">' + (candidates.length === 1 ? 'Matching bill' : candidates.length + ' matching bills — pick one') + '</div>';
      html += '<div class="ap-inbox-qbo-candidates">';
      candidates.forEach(function(c) {
        var id = String(c.txnId);
        var disabled = !!c.otherDriveUrl;
        var checked = state.qboSelectedTxnId === id;
        html += '<label class="ap-inbox-qbo-candidate' + (checked ? ' ap-inbox-qbo-candidate-selected' : '') +
          (disabled ? ' ap-inbox-qbo-candidate-disabled' : '') + '">';
        html += '<input type="radio" name="ap-inbox-qbo-candidate" value="' + esc(id) + '"' +
          (checked ? ' checked' : '') + (disabled ? ' disabled' : '') + '>';
        html += '<span class="ap-inbox-qbo-candidate-text">' + esc(c.human || ('Bill ' + id));
        html += ' <span class="ap-inbox-qbo-tag" title="QBO Bill Id">' + esc(id) + '</span>';
        if (disabled) html += ' <span class="ap-inbox-qbo-tag ap-inbox-qbo-tag-warn">linked to another scan</span>';
        else if (c.alreadyLinked) html += ' <span class="ap-inbox-qbo-tag">already linked</span>';
        // Memo carries Fishbowl's postpo fingerprint — verified = Fishbowl owns the record
        if (c.fishbowlPo) {
          html += c.fishbowlPo.verified
            ? ' <span class="ap-inbox-qbo-tag ap-inbox-qbo-tag-po" title="Verified in Fishbowl">Fishbowl PO ' + esc(c.fishbowlPo.num || c.fishbowlPo.core) + '</span>'
            : ' <span class="ap-inbox-qbo-tag ap-inbox-qbo-tag-warn" title="Memo PO did not verify — see warnings">memo PO ' + esc(c.fishbowlPo.core) + ' not ours</span>';
        }
        html += '</span>';
        if (checked && c.privateNote) {
          var memo = String(c.privateNote);
          if (memo.length > 200) memo = memo.slice(0, 200) + '…';
          html += '<div class="ap-inbox-qbo-memo"><span class="ap-inbox-chip-label">Existing memo</span> ' + esc(memo) + '</div>';
        }
        html += '</label>';
      });
      html += '</div>';
    } else {
      html += '<div class="ap-inbox-nomatch"><div class="ap-inbox-nomatch-title">' +
        esc(errors[0] || 'No QBO bill matches.') + '</div>';
      // With an Invoice # the connector matches on Bill no. only (no date/amount
      // fallback) — a misread description in that field hides the real bill
      html += '<p>' + (state.qbo.docNumber
        ? 'Matched on Bill no. only. If the QBO bill has no Bill no., clear Invoice # and try again.'
        : 'Fix bill date / amount above and try again.') + '</p></div>';
      if (nearby.length) {
        html += '<div class="ap-inbox-qbo-section-title">Nearby bills (±7 days, display only)</div>';
        html += '<ul class="ap-inbox-qbo-nearby">';
        nearby.forEach(function(n) {
          html += '<li>' + esc(n.human || ('Bill ' + n.txnId)) +
            ' <span class="ap-inbox-qbo-tag" title="QBO Bill Id">' + esc(n.txnId) + '</span></li>';
        });
        html += '</ul>';
      }
    }

    // Files to — Admin API filing block
    html += '<div class="ap-inbox-qbo-files-to">';
    var verifiedPo = qboVerifiedPo();
    if (data.retry) {
      // Half state: the PDF is already where it is going to stay
      html += '<div class="ap-inbox-qbo-section-title">Files to</div>';
      html += '<p>' + esc(displayPath(filing.destinationPath) || '—') +
        (filing.fileName ? ' · ' + esc(filing.fileName) : '') +
        ' <span class="ap-inbox-qbo-tag">' + esc(FILING_STATUS_NOTE.filed || 'already filed') + '</span></p>';
      html += '<p class="ap-inbox-form-hint">PDF already filed — Link &amp; file stamps QBO only.</p>';
    } else if (verifiedPo) {
      // Verified Fishbowl PO → choice, PO folder default (§4: Fishbowl owns the record)
      var poFiling = verifiedPo.filing || {};
      html += '<div class="ap-inbox-qbo-section-title">Files to — this bill came from a Fishbowl PO</div>';
      html += '<div class="ap-inbox-qbo-candidates">';
      html += '<label class="ap-inbox-qbo-candidate' + (state.qboFileTo === 'po' ? ' ap-inbox-qbo-candidate-selected' : '') + '">';
      html += '<input type="radio" name="ap-inbox-qbo-fileto" value="po"' + (state.qboFileTo === 'po' ? ' checked' : '') + '>';
      html += '<span class="ap-inbox-qbo-candidate-text">PO folder · ' + esc(displayPath(poFiling.destinationPath) || 'POs/…') +
        (poFiling.fileName ? ' · ' + esc(poFiling.fileName) : '') +
        ' <span class="ap-inbox-qbo-tag ap-inbox-qbo-tag-po">PO ' + esc(verifiedPo.num || verifiedPo.core) + (verifiedPo.vendorName ? ' · ' + esc(verifiedPo.vendorName) : '') + '</span>' +
        '<br><small>Fishbowl owns the record — marks "Accounting has invoice" (CF 268) and stamps the bill.</small></span>';
      html += '</label>';
      html += '<label class="ap-inbox-qbo-candidate' + (state.qboFileTo === 'vendor' ? ' ap-inbox-qbo-candidate-selected' : '') + '">';
      html += '<input type="radio" name="ap-inbox-qbo-fileto" value="vendor"' + (state.qboFileTo === 'vendor' ? ' checked' : '') + '>';
      html += '<span class="ap-inbox-qbo-candidate-text">Vendor folder · ' + esc(displayPath(filing.destinationPath) || '—') +
        (filing.fileName ? ' · ' + esc(filing.fileName) : '') +
        (FILING_STATUS_NOTE[filing.status] ? ' <span class="ap-inbox-qbo-tag">' + esc(FILING_STATUS_NOTE[filing.status]) + '</span>' : '') +
        '<br><small>Treat as a bill with no Fishbowl record (the PO stays unmarked).</small></span>';
      html += '</label>';
      html += '</div>';
      if (state.qboFileTo === 'vendor' && filing.status === 'refuse') {
        html += '<p class="ap-inbox-form-hint">' + esc(filing.error || 'The folder map could not choose a folder.') + '</p>';
        html += renderQboFolderPicker(filing);
      }
    } else if (filing.status === 'refuse') {
      html += '<div class="ap-inbox-qbo-section-title">Files to — pick the folder</div>';
      html += '<p class="ap-inbox-form-hint">' + esc(filing.error || 'The folder map could not choose a folder.') + '</p>';
      html += renderQboFolderPicker(filing);
    } else {
      html += '<div class="ap-inbox-qbo-section-title">Files to</div>';
      html += '<p>' + esc(displayPath(filing.destinationPath) || '—') +
        (filing.fileName ? ' · ' + esc(filing.fileName) : '') +
        (FILING_STATUS_NOTE[filing.status] ? ' <span class="ap-inbox-qbo-tag">' + esc(FILING_STATUS_NOTE[filing.status]) + '</span>' : '') + '</p>';
    }
    html += '</div>';

    if (warnings.length) {
      html += '<ul class="ap-inbox-qbo-warnings">';
      warnings.forEach(function(w) { html += '<li>' + esc(w) + '</li>'; });
      html += '</ul>';
    }
    return html;
  }

  function qboLinkButton() {
    var shell = document.getElementById(QBO_MODAL_ID);
    return shell ? shell.querySelector('[data-action="qbo-link"]') : null;
  }

  function openQboLinkModal(item) {
    if (!window.MWACL || !window.MWACL.Utils || typeof window.MWACL.Utils.mountModal !== 'function') {
      toast('Modal helper not loaded', 'error');
      return;
    }
    var el = window.MWACL.Utils.mountModal(QBO_MODAL_ID, {
      title: 'Link to QBO bill',
      bodyHtml: '<div id="ap-inbox-qbo-body"></div>',
      footerHtml:
        window.MWACL.Utils.modalButton('Cancel', null, 'secondary', true) +
        window.MWACL.Utils.modalButton('Link &amp; file', 'qbo-link', 'primary')
    });

    function refresh() {
      var body = document.getElementById('ap-inbox-qbo-body');
      if (!body) return;
      body.innerHTML = renderQboModalBody(item);
      body.querySelectorAll('input[name="ap-inbox-qbo-candidate"]').forEach(function(r) {
        r.addEventListener('change', function() {
          state.qboSelectedTxnId = r.value;
          state.qboFileTo = defaultQboFileTo(); // a different bill may or may not carry a verified PO
          refresh();
        });
      });
      body.querySelectorAll('input[name="ap-inbox-qbo-fileto"]').forEach(function(r) {
        r.addEventListener('change', function() { state.qboFileTo = r.value; refresh(); });
      });
      body.querySelectorAll('input[name="ap-inbox-qbo-folder"]').forEach(function(r) {
        r.addEventListener('change', function() { state.qboFolderId = r.value; refresh(); });
      });
      var btn = qboLinkButton();
      if (btn) btn.disabled = state.filing || !qboLinkReady();
    }
    refresh();

    window.MWACL.Utils.wireModalAction(el, 'qbo-link', function() {
      executeQboLink(item);
    });
  }

  function closeQboLinkModal() {
    if (document.getElementById(QBO_MODAL_ID)) window.MWACL.Modal.close(QBO_MODAL_ID);
  }

  // In-place row patch (JARED): half state changes one row's Reason — no re-fetch, no table re-render
  function patchRowReason(item) {
    var row = document.querySelector('.ap-inbox-row[data-doc-id="' + item.id + '"]');
    if (!row) return;
    var cells = row.querySelectorAll('td');
    var cell = cells[cells.length - 1];
    if (cell) {
      cell.textContent = reasonLabel(item.reviewReason);
      cell.setAttribute('title', reasonLabel(item.reviewReason));
    }
  }

  function executeQboLink(item) {
    if (!item || isBusy() || !qboLinkReady()) return;
    var btn = qboLinkButton();
    state.filing = true;
    if (btn) { btn.disabled = true; btn.textContent = 'Linking…'; }
    renderPane();

    var payload = qboLinkPayload(item);
    payload.apply = true;
    payload.txnId = state.qboSelectedTxnId;
    // fileTo 'po' only when the chosen bill's memo PO verified (server re-checks on the fresh preview)
    payload.fileTo = qboPoMode() ? 'po' : 'vendor';
    if (payload.fileTo === 'vendor' && state.qboFolderId) payload.folderId = state.qboFolderId;

    window.MWACL.API.call('ap_link_qbo_entry', payload)
      .then(function(response) {
        closeQboLinkModal();
        afterFiled(item, response); // resets state.filing, removes row in place, auto-advances
      })
      .catch(function(err) {
        state.filing = false;
        console.error('[ap-inbox-review] QBO link error:', err);
        if (err.errorCode === HALF_STATE_REASON) {
          // PDF moved, QBO stamp failed — row stays; patch in place, retry stamps only
          var details = (err.responseData && err.responseData.details) || {};
          item.reviewReason = HALF_STATE_REASON;
          if (details.destinationPath) item.destinationPath = details.destinationPath;
          if (details.productionFileId) item.productionFileId = details.productionFileId;
          if (details.driveLink) item.driveLink = details.driveLink;
          closeQboLinkModal();
          patchRowReason(item);
          state.qboPreview = null;
          renderPane();
          toast(err.message || 'PDF filed, but the QBO link failed — retry stamps QBO only', 'error');
          return;
        }
        // Other refusal (stale preview, folder refuse, vendor/amount mismatch): modal stays open
        if (btn) { btn.disabled = !qboLinkReady(); btn.textContent = 'Link & file'; }
        renderPane();
        toast(err.message || 'QBO link failed', 'error');
      });
  }

  function handleFile() {
    var item = selectedItem();
    if (!item || isBusy()) return;
    var po = (state.chosenPo || '').trim();
    if (!/\d{6}/.test(po)) {
      toast('Enter a 6-digit Fishbowl PO number', 'warning');
      return;
    }

    state.filing = true;
    renderPane();

    window.MWACL.API.call('ap_refile_reviewed_document', { docId: item.id, destination: 'po', poNumber: po })
      .then(function(response) {
        afterFiled(item, response);
      })
      .catch(function(err) {
        state.filing = false;
        renderPane();
        console.error('[ap-inbox-review] File error:', err);
        toast(err.message || 'File failed', 'error');
      });
  }

  function handleFileStatement(destination) {
    var item = selectedItem();
    if (!item || isBusy()) return;
    var s = state.stmt;
    if (destination === 'vendor_statement') {
      if (!s.vendorId || !s.vendorDisplayName) { toast('Pick the QuickBooks vendor', 'warning'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s.closeDate)) { toast('Close date must be YYYY-MM-DD', 'warning'); return; }
    } else {
      if (!s.accountFolderId) { toast('Pick the account folder', 'warning'); return; }
      if (!s.accountName) { toast('Account name is required', 'warning'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s.closeDate)) { toast('Close date must be YYYY-MM-DD', 'warning'); return; }
    }

    state.filing = true;
    renderPane();

    var payload;
    if (destination === 'vendor_statement') {
      payload = {
        docId: item.id,
        destination: 'vendor_statement',
        vendorId: s.vendorId,
        vendorDisplayName: s.vendorDisplayName,
        closeDate: s.closeDate,
      };
      if (s.folderId) payload.folderId = s.folderId;
      if (s.forceCreate) payload.forceCreate = true;
    } else {
      payload = {
        docId: item.id,
        destination: 'bank_statement',
        accountFolderId: s.accountFolderId,
        accountName: s.accountName,
        closeDate: s.closeDate,
        kind: s.kind || 'bank',
      };
    }

    window.MWACL.API.call('ap_refile_reviewed_document', payload)
      .then(function(response) {
        afterFiled(item, response);
      })
      .catch(function(err) {
        state.filing = false;
        var filing = (err.responseData && err.responseData.details && err.responseData.details.filing)
          || (err.responseData && err.responseData.filing)
          || null;
        if (filing && filing.candidates) {
          state.stmt.filing = filing;
          state.stmt.folderId = '';
          state.stmt.forceCreate = false;
        }
        renderPane();
        console.error('[ap-inbox-review] Statement file error:', err);
        toast(err.message || 'File failed', 'error');
      });
  }

  function handleCheckNow(docId, btn) {
    if (!docId || state.checkingCoverage) return;
    var item = state.items.find(function(i) { return i.id === docId; });
    var prevStatus = item ? item.statementStatus : null;
    state.checkingCoverage = docId;
    var origText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Checking…';
    window.MWACL.API.call('ap_check_statement_coverage', { docId: docId })
      .then(function(response) {
        var data = response.data || response;
        state.checkingCoverage = null;
        if (item) {
          item.statementStatus = data.statementStatus || item.statementStatus;
          item.statementCoverage = data.statementCoverage || item.statementCoverage;
          item.statementCheckedAt = data.statementCoverage && data.statementCoverage.checkedAt
            ? data.statementCoverage.checkedAt
            : item.statementCheckedAt;
        }
        applyCoverageStatusDelta(prevStatus, item && item.statementStatus);
        updateFiledStatementRow(item || { id: docId, statementStatus: data.statementStatus, statementCoverage: data.statementCoverage });
        toast(data.message || 'Check complete', 'success');
        if (state.selectedId === docId) renderPane();
      })
      .catch(function(err) {
        state.checkingCoverage = null;
        btn.disabled = false;
        btn.textContent = origText;
        console.error('[ap-inbox-review] Check now error:', err);
        toast(err.message || 'Coverage check failed', 'error');
      });
  }

  function handleMarkReviewed(docId, btn) {
    if (!docId || state.markingReviewed) return;
    var item = state.items.find(function(i) { return i.id === docId; });
    state.markingReviewed = docId;
    var origText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';
    window.MWACL.API.call('ap_update_statement_status', { docId: docId, statementStatus: 'reviewed' })
      .then(function() {
        state.markingReviewed = null;
        var row = document.querySelector('.ap-inbox-row[data-id="' + docId + '"]') ||
          document.querySelector('.ap-inbox-row[data-doc-id="' + docId + '"]');
        if (row) row.remove();
        state.items = state.items.filter(function(i) { return i.id !== docId; });
        if (item && item.statementStatus === 'needs_attention') {
          state.stats.needsAttention = Math.max(0, (state.stats.needsAttention || 1) - 1);
        } else {
          state.stats.filedStatements = Math.max(0, (state.stats.filedStatements || 1) - 1);
        }
        if (state.selectedId === docId) {
          state.selectedId = null;
          renderPane();
        }
        renderStats();
        if (!state.items.length) renderTable();
      })
      .catch(function(err) {
        state.markingReviewed = null;
        btn.disabled = false;
        btn.textContent = origText;
        console.error('[ap-inbox-review] Mark reviewed error:', err);
        toast(err.message || 'Could not mark reviewed', 'error');
      });
  }

  function handleFileBanking(destination) {
    var item = selectedItem();
    if (!item || isBusy()) return;
    if (destination === 'deposits' && state.isBatch) {
      toast('Deposits must be one check per PDF — uncheck Batch', 'warning');
      return;
    }
    if (!state.isBatch && !state.checkNumber) {
      toast('Enter a check number (or mark Batch for Check Stubs)', 'warning');
      return;
    }

    state.filing = true;
    renderPane();

    window.MWACL.API.call('ap_refile_reviewed_document', {
      docId: item.id,
      destination: destination,
      checkNumber: state.checkNumber,
      payee: state.payee,
      reference: state.reference,
      checkDate: state.checkDate,
      isBatch: destination === 'check_stubs' && state.isBatch,
    })
      .then(function(response) {
        afterFiled(item, response);
      })
      .catch(function(err) {
        state.filing = false;
        renderPane();
        console.error('[ap-inbox-review] Banking file error:', err);
        toast(err.message || 'File failed', 'error');
      });
  }

  function isBusy() {
    return state.filing || state.removing || state.pulling || state.reclassifying || state.autofiling || !!state.checkingCoverage;
  }

  function setHeaderBusy() {
    var pull = document.getElementById('ap-inbox-pull');
    if (pull) {
      pull.disabled = isBusy();
      pull.textContent = state.pulling ? 'Pulling…' : 'Pull from Inbox';
    }
    var reclass = document.getElementById('ap-inbox-reclassify');
    if (reclass && !state.reclassifying) {
      reclass.disabled = isBusy();
    }
    var autofile = document.getElementById('ap-inbox-autofile');
    if (autofile && !state.autofiling) {
      autofile.disabled = isBusy();
    }
    renderReclassifyLabel();
  }

  // Shared confirm through mountModal (Not ours, Pull, Auto-file)
  function openConfirmModal(opts) {
    if (!window.MWACL || !window.MWACL.Utils || typeof window.MWACL.Utils.mountModal !== 'function') {
      toast('Modal helper not loaded', 'error');
      return;
    }
    var el = window.MWACL.Utils.mountModal(opts.id, {
      title: opts.title,
      maxWidth: '400px',
      draggable: false,
      resizable: false,
      bodyHtml: opts.bodyHtml ? opts.bodyHtml : '<p>' + esc(opts.body) + '</p>',
      footerHtml:
        window.MWACL.Utils.modalButton('Cancel', null, 'secondary', true) +
        window.MWACL.Utils.modalButton(esc(opts.confirmLabel), 'confirm', 'primary')
    });
    window.MWACL.Utils.wireModalAction(el, 'confirm', function() {
      window.MWACL.Modal.close(opts.id);
      opts.onConfirm();
    });
  }

  function confirmNotOurs() {
    var item = selectedItem();
    if (!item || isBusy()) return;
    openConfirmModal({
      id: 'ap-inbox-confirm-not-ours',
      title: 'Not a MindWorks AP document?',
      body: 'Move this scan to Mailroom/_Not_Ours and remove it from the review queue. The file is kept; the same scan will not come back.',
      confirmLabel: 'Not ours',
      onConfirm: function() { executeNotOurs(item); }
    });
  }

  function executeNotOurs(item) {
    if (!item || isBusy()) return;
    state.removing = true;
    renderPane();

    window.MWACL.API.call('ap_remove_inbox_review', { docId: item.id })
      .then(function(response) {
        state.removing = false;
        afterFiled(item, response);
      })
      .catch(function(err) {
        state.removing = false;
        renderPane();
        console.error('[ap-inbox-review] Not ours error:', err);
        toast(err.message || 'Could not remove from queue', 'error');
      });
  }

  // Remove filed/dismissed row in-place, then auto-advance to the next doc in the filtered view
  function afterFiled(item, response) {
    if (state.inspect && state.inspect.id === item.id) {
      if (state.inspect.url) {
        try { URL.revokeObjectURL(state.inspect.url); } catch (e) { /* already gone */ }
      }
      state.inspect = null;
    }
    var data = response.data || response;
    toast(data.message || 'Filed', 'success');

    var visible = filteredItems();
    var idx = visible.findIndex(function(i) { return i.id === item.id; });
    var next = visible[idx + 1] || visible[idx - 1] || null;

    state.items = state.items.filter(function(i) { return i.id !== item.id; });
    state.selectedIds.delete(item.id);
    if (state.view === 'filed_statements') {
      state.stats.filedStatements = Math.max(0, (state.stats.filedStatements || 1) - 1);
    } else {
      state.stats.needsReview = Math.max(0, (state.stats.needsReview || 1) - 1);
    }
    state.filing = false;
    state.candidatesForId = null;
    state.candidates = [];
    state.noMatch = null;
    renderStats();

    if (next) {
      selectDoc(next.id); // renders table + pane
    } else {
      state.selectedId = null;
      state.chosenPo = '';
      state.checkNumber = '';
      state.payee = '';
      state.reference = '';
      state.checkDate = '';
      state.isBatch = false;
      state.fileAs = '';
      state.qbo = { vendorName: '', vendorId: '', docNumber: '', txnDate: '', total: '' };
      state.qboPreview = null;
      state.qboSelectedTxnId = '';
      state.qboFolderId = '';
      state.qboFileTo = '';
      state.stmt = emptyStmt(null);
      renderTable();
      renderPane();
    }
  }

  function confirmPullInbox() {
    if (isBusy()) return;
    openConfirmModal({
      id: 'ap-inbox-confirm-pull',
      title: 'Pull from Drive Inbox?',
      body: 'Pull up to 25 files from the Drive Inbox? Confident matches file straight to PO folders or Check Stubs / Deposits; the rest land here for review. This can take several minutes — the page will wait until it finishes.',
      confirmLabel: 'Pull',
      onConfirm: executePullInbox
    });
  }

  function pullSummaryToast(data) {
    if (!data.summary && (data.processed === 0 || data.message === 'Inbox is empty')) {
      return data.message || 'Inbox is empty';
    }
    var s = data.summary || {};
    var parts = [];
    if (s.filed) parts.push(s.filed + ' filed');
    if (s.needsReview) parts.push(s.needsReview + ' queued');
    if (s.skippedDuplicates) parts.push(s.skippedDuplicates + ' duplicate(s) skipped');
    if (s.potentialDuplicates) parts.push(s.potentialDuplicates + ' possible duplicate(s)');
    if (s.errors) parts.push(s.errors + ' error(s)');
    var remaining = (s.inboxTotal != null && s.considered != null)
      ? Math.max(0, s.inboxTotal - s.considered) : 0;
    if (remaining) parts.push(remaining + ' still in Inbox');
    var msg = data.message || 'Inbox processing complete';
    return parts.length ? msg + ' — ' + parts.join(', ') : msg;
  }

  function pollApJob(action, jobId, onProgress, label) {
    var POLL_INTERVAL = 2000;
    var MAX_POLLS = 300; // 10 minutes — matches Cloud Run timeout
    var MAX_CONSECUTIVE_FAILURES = 4; // tolerate blips / a poll hitting the wrong instance
    var jobLabel = label || 'Job';
    var i = 0;
    var consecutiveFailures = 0;

    return new Promise(function(resolve, reject) {
      function next(err) {
        i += 1;
        if (i >= MAX_POLLS) {
          reject(err || new Error(jobLabel + ' timed out after 10 minutes'));
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      }

      function tick() {
        window.MWACL.API.call(action, { jobId })
          .then(function(progress) {
            consecutiveFailures = 0;
            var p = progress.data || progress;
            if (p.status === 'complete') {
              resolve(p.result || p);
              return;
            }
            if (p.status === 'error') {
              reject(new Error(p.message || (jobLabel + ' failed')));
              return;
            }
            if (onProgress && p.message) onProgress(p.message);
            next();
          })
          .catch(function(err) {
            // The job keeps running server-side — don't abort the wait on a
            // transient poll failure (network blip, poll routed to a Cloud Run
            // instance that doesn't hold the in-memory job).
            consecutiveFailures += 1;
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
              reject(err);
              return;
            }
            console.warn('[ap-inbox-review] Poll failed (' + consecutiveFailures + '/' + MAX_CONSECUTIVE_FAILURES + '), retrying:', err.message);
            next(err);
          });
      }
      setTimeout(tick, POLL_INTERVAL);
    });
  }

  function executePullInbox() {
    if (isBusy()) return;
    state.pulling = true;
    setHeaderBusy();
    renderPane();

    // Shared progress modal (T2T: same spinner + step + timer as standards parse/import)
    var progress = (window.MWACL && window.MWACL.Utils && window.MWACL.Utils.showJobProgressModal)
      ? window.MWACL.Utils.showJobProgressModal({
          title: 'Pulling from Drive Inbox',
          step: 'Starting inbox pull\u2026',
          hint: 'List inbox \u2192 AI classification \u2192 PO lookup \u2192 file to folders'
        })
      : null;

    window.MWACL.API.call('ap_process_drive_inbox', { dryRun: false, maxFiles: 25, async: true })
      .then(function(response) {
        var data = response.data || response;
        if (data.async && data.jobId) {
          return pollApJob('ap_get_inbox_pull_progress', data.jobId, progress ? progress.setStep : null, 'Pull');
        }
        return data;
      })
      .then(function(data) {
        if (progress) progress.close();
        state.pulling = false;
        setHeaderBusy();
        renderPane(); // re-enable pane buttons — loadData only re-renders the pane if the selected doc vanished
        toast(pullSummaryToast(data), 'success');
        loadData();
      })
      .catch(function(err) {
        if (progress) progress.close();
        state.pulling = false;
        setHeaderBusy();
        renderPane();
        console.error('[ap-inbox-review] Pull from Inbox error:', err);
        toast(err.message || 'Pull from Inbox failed', 'error');
      });
  }

  // Reclassify the selected review-queue documents (no moves, no status change).
  // Async job + poll — do not wrap the old sync POST in a spinner (Chrome ~300s).
  function handleReclassify() {
    if (isBusy()) return;
    var docIds = Array.from(state.selectedIds);
    if (!docIds.length) {
      toast('Select documents to reclassify', 'info');
      return;
    }
    state.reclassifying = true;
    var btn = document.getElementById('ap-inbox-reclassify');
    if (btn) { btn.disabled = true; btn.textContent = 'Reclassifying…'; }
    setHeaderBusy();

    var progress = (window.MWACL && window.MWACL.Utils && window.MWACL.Utils.showJobProgressModal)
      ? window.MWACL.Utils.showJobProgressModal({
          title: 'Reclassify',
          step: 'Starting reclassify\u2026',
          hint: 'Download from Drive \u2192 classify \u2192 update review fields'
        })
      : null;

    window.MWACL.API.call('ap_reclassify_inbox_review', { async: true, docIds: docIds })
      .then(function(response) {
        var data = response.data || response;
        if (data.async && data.jobId) {
          return pollApJob('ap_get_reclassify_progress', data.jobId, progress ? progress.setStep : null, 'Reclassify');
        }
        return data;
      })
      .then(function(data) {
        if (progress) progress.close();
        state.reclassifying = false;
        if (btn) btn.disabled = false;
        state.selectedIds.clear();
        setHeaderBusy();
        var s = data.summary || {};
        toast((data.message || 'Reclassified') + (s.typeChanged ? ' — ' + s.typeChanged + ' type change(s)' : '') + (s.skipped ? ' — ' + s.skipped + ' skipped' : ''), 'success');
        state.selectedId = null;
        loadData();
      })
      .catch(function(err) {
        if (progress) progress.close();
        state.reclassifying = false;
        if (btn) btn.disabled = false;
        setHeaderBusy();
        console.error('[ap-inbox-review] Reclassify error:', err);
        toast(err.message || 'Reclassify failed', 'error');
      });
  }

  function restoreAutofileBtn(btn, origText) {
    state.autofiling = false;
    if (btn) { btn.disabled = false; btn.textContent = origText || 'Auto-file ready'; }
    setHeaderBusy();
  }

  function autoFileItemLine(it, withReason) {
    var po = it.po ? 'PO# ' + it.po : 'PO# —';
    var vendor = it.vendor || '—';
    var seller = it.marketplaceSeller ? ' (seller: ' + it.marketplaceSeller + ')' : '';
    var line = po + ' · ' + vendor + seller;
    if (withReason) {
      line += ' · ' + (REASON_LABELS[it.reason] || it.reason || 'skipped');
    } else if (it.fileName) {
      line += ' · ' + it.fileName;
    }
    return line;
  }

  function handleAutoFileReady() {
    if (isBusy()) return;
    if (document.getElementById('ap-inbox-confirm-autofile')) return;
    state.autofiling = true;
    var btn = document.getElementById('ap-inbox-autofile');
    var origText = btn ? btn.textContent : 'Auto-file ready';
    if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
    setHeaderBusy();

    window.MWACL.API.call('ap_auto_file_ready_inbox_review', { apply: false })
      .then(function(response) {
        var data = response.data || response;
        var summary = data.summary || {};
        if (!summary.ready) {
          toast(data.message || 'No rows ready to auto-file', 'info');
          restoreAutofileBtn(btn, origText);
          return;
        }
        restoreAutofileBtn(btn, origText);
        openAutoFileConfirm(data);
      })
      .catch(function(err) {
        restoreAutofileBtn(btn, origText);
        console.error('[ap-inbox-review] Auto-file preview error:', err);
        toast(err.message || 'Auto-file preview failed', 'error');
      });
  }

  function autoFileConfirmCopy(n) {
    var cap = AUTO_FILE_CAP;
    if (n > cap) {
      return {
        sentence: 'Up to ' + cap + ' of these ' + n + ' rows are filed this run (Drive move + Fishbowl "has invoice" flag). The rest stay in the queue for another run. Skipped rows stay in the queue.',
        confirmLabel: 'File ' + cap + ' of ' + n + ' rows'
      };
    }
    return {
      sentence: n + ' row(s) will be filed to their PO folders (Drive move + Fishbowl "has invoice" flag). Skipped rows stay in the queue.',
      confirmLabel: 'File ' + n + ' rows'
    };
  }

  function openAutoFileConfirm(data) {
    var items = data.items || [];
    var would = items.filter(function(it) { return it.status === 'would_file'; });
    var skipped = items.filter(function(it) { return it.status === 'skipped'; });
    var n = would.length;
    var copy = autoFileConfirmCopy(n);
    var html = '<p>' + esc(copy.sentence) + '</p>';
    html += '<ul>';
    would.forEach(function(it) {
      html += '<li>' + esc(autoFileItemLine(it, false)) + '</li>';
    });
    html += '</ul>';
    if (skipped.length) {
      html += '<p>Skipped</p><ul>';
      skipped.forEach(function(it) {
        html += '<li>' + esc(autoFileItemLine(it, true)) + '</li>';
      });
      html += '</ul>';
    }
    openConfirmModal({
      id: 'ap-inbox-confirm-autofile',
      title: 'Auto-file ready rows',
      bodyHtml: html,
      confirmLabel: copy.confirmLabel,
      onConfirm: executeAutoFileReady
    });
  }

  function executeAutoFileReady() {
    if (isBusy()) return;
    state.autofiling = true;
    var btn = document.getElementById('ap-inbox-autofile');
    var origText = 'Auto-file ready';
    if (btn) { btn.disabled = true; btn.textContent = 'Filing…'; }
    setHeaderBusy();

    window.MWACL.API.call('ap_auto_file_ready_inbox_review', { apply: true })
      .then(function(response) {
        var data = response.data || response;
        var errors = (data.summary && data.summary.errors) || 0;
        toast((data.message || 'Filed') + (errors ? ' — ' + errors + ' error(s)' : ''), 'success');
        restoreAutofileBtn(btn, origText);
        loadData();
      })
      .catch(function(err) {
        restoreAutofileBtn(btn, origText);
        console.error('[ap-inbox-review] Auto-file apply error:', err);
        toast(err.message || 'Auto-file failed', 'error');
      });
  }

  async function waitAndInit() {
    if (window.MWACL && window.MWACL.Utils && window.MWACL.Utils.waitForAuth) {
      await window.MWACL.Utils.waitForAuth();
    }
    await new Promise(function(r) { setTimeout(r, 300); });
    init();
  }

  window.MWACLAPInboxReview = { init: init, reload: function() { loadData(); } };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitAndInit);
  } else {
    waitAndInit();
  }

  console.log(`[mw_acl-ap-inbox-review.js] Module loaded - ${MODULE_VERSION}`);
})();
